import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { diffArrays, diffChars } from 'diff';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useModels, modelKey, shortModel, ModelsLegend } from './modelInfo';

// THE dialog of one point — one chronological feed across both phases:
//   task phase (task_loop): generator cards <-> human comments -> the decision
//   writing phase (critic_loop): brief -> author <-> critic/fact -> formalizer
// The writing phase is a BRANCH TREE: any agent turn can be regenerated with an
// edited system/user prompt into a sibling variant; the arrows switch between
// siblings and the downstream follows the active branch (materialized server-
// side into loop.messages). Under the hood the agents share NO context — the
// generator and author each hold their own session, the reviewers are stateless.

const MD_COMPONENTS = {
  p: (props) => <p style={{ margin: '0 0 6px' }} {...props} />,
  ul: (props) => <ul style={{ margin: '4px 0', paddingLeft: 18 }} {...props} />,
  ol: (props) => <ol style={{ margin: '4px 0', paddingLeft: 18 }} {...props} />,
  li: (props) => <li style={{ margin: '2px 0' }} {...props} />,
  h1: (props) => <h1 style={{ fontSize: '1.15em', margin: '6px 0' }} {...props} />,
  h2: (props) => <h2 style={{ fontSize: '1.1em', margin: '6px 0' }} {...props} />,
  h3: (props) => <h3 style={{ fontSize: '1.05em', margin: '6px 0' }} {...props} />,
  code: (props) => <code className="pd-code-inline" {...props} />,
  blockquote: (props) => <blockquote className="pd-quote" {...props} />,
  a: (props) => <a target="_blank" rel="noreferrer" {...props} />,
};
const Md = ({ children }) => (
  <Markdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{children || ''}</Markdown>
);

async function papi(path, body) {
  const res = await fetch(`/api/pipeline${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (e) {}
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return time;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}, ${time}`;
};

const isNearBottom = (el) => (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;

const promptVer = (p) => (p && p.version ? `v${p.version}` : null);

// Which record holds a given agent's prompt snapshot / model / version.
const promptOf = (writeLoop, taskLoop, role) => (
  role === 'tres' ? (taskLoop && taskLoop.research_prompt)
    : role === 'tgen' ? (taskLoop && taskLoop.gen_prompt)
    : role === 'tcheck' ? (taskLoop && taskLoop.check_prompt)
    : role === 'tfact' ? (taskLoop && taskLoop.fact_prompt)
    : role === 'critic' ? (writeLoop && writeLoop.critic_prompt)
    : role === 'factcheck' ? (writeLoop && writeLoop.fact_prompt)
    : role === 'formal' ? (writeLoop && writeLoop.formal_prompt)
    : (writeLoop && writeLoop.writer_prompt)
);
const modelOf = (writeLoop, taskLoop, role) => (
  role === 'tres' ? (taskLoop && taskLoop.research_model)
    : role === 'tgen' ? (taskLoop && taskLoop.model)
    : role === 'tcheck' ? (taskLoop && taskLoop.gate_model)
    : role === 'tfact' ? (taskLoop && taskLoop.fact_model)
    : role === 'critic' ? (writeLoop && writeLoop.critic_model)
    : role === 'factcheck' ? (writeLoop && writeLoop.fact_model)
    : role === 'formal' ? (writeLoop && writeLoop.formalizer_model)
    : (writeLoop && writeLoop.writer_model)
);

// role -> label + chat side + rail token. No emoji: identity is the rail + chip.
const ROLE = {
  writer: { label: 'Author', side: 'left', rail: 'var(--pd-role-author)' },
  critic: { label: 'Critic', side: 'right', rail: 'var(--pd-role-critic)' },
  factcheck: { label: 'Fact-checker', side: 'right', rail: 'var(--pd-role-fact)' },
  formal: { label: 'Formalizer', side: 'full', rail: 'var(--pd-role-formal)' },
  brief: { label: 'Brief', side: 'full', rail: 'var(--pd-role-muted)' },
  human: { label: 'Editor', side: 'right', rail: 'var(--pd-role-muted)' },
  tbrief: { label: 'Task brief', side: 'full', rail: 'var(--pd-role-muted)' },
  tres: { label: 'Researcher', side: 'left', rail: 'var(--pd-role-gen)' },
  tgen: { label: 'Task generator', side: 'left', rail: 'var(--pd-role-gen)' },
  tcheck: { label: 'Checklist', side: 'full', rail: 'var(--pd-role-check)' },
  tfact: { label: 'Task-Checker', side: 'right', rail: 'var(--pd-role-fact)' },
};

const WRITE_STATUS = {
  running: { label: 'running', kind: 'accent' },
  approved: { label: 'approved', kind: 'ok' },
  max_rounds: { label: 'round cap reached', kind: 'warn' },
  stopped: { label: 'stopped', kind: 'muted' },
  interrupted: { label: 'interrupted', kind: 'muted' },
  paused: { label: 'paused — your move', kind: 'warn' },
  error: { label: 'error', kind: 'danger' },
};
const TASK_STATUS = {
  running: { label: 'running…', kind: 'accent' },
  awaiting_choice: { label: 'cards optional — no task by default', kind: 'muted' },
  chosen: { label: 'card chosen', kind: 'ok' },
  no_task: { label: 'no task', kind: 'muted' },
  stopped: { label: 'stopped', kind: 'muted' },
  interrupted: { label: 'interrupted', kind: 'muted' },
  error: { label: 'error', kind: 'danger' },
};

const TYPE_KIND = { 'НА МЕСТЕ': 'ok', 'ЭРУДИЦИЯ': 'accent', 'ЛОГИКА': 'warn' };

const LIVE_DELIM_RE = /^===\s*КАНДИДАТ\s+(\d+)\s*===\s*$/;
const LIVE_FIELD_RE = /^(ВОПРОС|ОТВЕТ|ТИП|ЗАЦЕПКА|ПЭЙ-ОФФ)\s*:\s*(.*)$/;
const LIVE_FIELD_KEYS = { 'ВОПРОС': 'question', 'ОТВЕТ': 'answer', 'ТИП': 'type', 'ПЭЙ-ОФФ': 'payoff' };
const LIVE_TYPES = ['НА МЕСТЕ', 'ЭРУДИЦИЯ', 'ЛОГИКА'];
function parseCandidatesText(text) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let cur = null;
  for (const line of lines) {
    const m = line.match(LIVE_DELIM_RE);
    if (m) { cur = { num: parseInt(m[1], 10), lines: [] }; blocks.push(cur); continue; }
    if (cur) cur.lines.push(line);
  }
  return blocks.map((b) => {
    const c = { num: b.num };
    let field = null;
    for (const line of b.lines) {
      const fm = line.match(LIVE_FIELD_RE);
      if (fm) { field = LIVE_FIELD_KEYS[fm[1]] || null; if (field) c[field] = fm[2].trim(); continue; }
      if (field && line.trim()) c[field] = `${c[field] ? `${c[field]}\n` : ''}${line.trim()}`;
    }
    if (c.type) { const t = c.type.toUpperCase(); c.type = LIVE_TYPES.find((k) => t.includes(k)) || c.type; }
    return c;
  });
}

// Live parse of the fact-checker's streaming answer into fact rows, so the cards
// build up on screen instead of showing raw ledger markup. Handles both output
// shapes: the round-1 full ledger (=== ФАКТ N === blocks) and the round-2+ edits
// (ADD | … lines). REMOVE lines and the fence markers are ignored — once the turn
// finishes the message re-renders through FactLedger with the full round diff.
const LIVE_FACT_BLOCK_RE = /^\s*===\s*ФАКТ\s+F?(\d+)\s*===\s*$/;
const LIVE_FACT_FIELD_RE = /^(УТВЕРЖДЕНИЕ|СТАТУС|ИСТОЧНИК|ЗАМЕТКА)\s*:\s*(.*)$/;
const LIVE_FACT_FIELD_KEYS = { 'УТВЕРЖДЕНИЕ': 'statement', 'СТАТУС': 'status', 'ИСТОЧНИК': 'source', 'ЗАМЕТКА': 'note' };
const LIVE_FACT_ADD_RE = /^\s*ADD\b\s*(.*)$/i;
function parseFactLedgerText(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  let cur = null;   // an open "=== ФАКТ N ===" block
  let field = null;
  const flush = () => { if (cur && cur.statement) out.push(cur); cur = null; field = null; };
  for (const raw of lines) {
    const bm = raw.match(LIVE_FACT_BLOCK_RE);
    if (bm) { flush(); cur = { id: parseInt(bm[1], 10) }; continue; }
    if (!cur) {
      const am = raw.match(LIVE_FACT_ADD_RE); // an edit-round ADD line (no block)
      if (am) {
        const e = { isNew: true };
        for (const seg of am[1].split('|')) {
          const fm = seg.trim().match(LIVE_FACT_FIELD_RE);
          if (fm) e[LIVE_FACT_FIELD_KEYS[fm[1]]] = fm[2].trim();
        }
        if (e.statement) out.push(e);
      }
      continue;
    }
    const fm = raw.match(LIVE_FACT_FIELD_RE);
    if (fm) { field = LIVE_FACT_FIELD_KEYS[fm[1]]; cur[field] = fm[2].trim(); continue; }
    if (/^\s*===/.test(raw)) { flush(); continue; } // a fence line closes the block
    if (field && raw.trim()) cur[field] = `${cur[field] ? `${cur[field]}\n` : ''}${raw.trim()}`;
  }
  flush();
  return out.filter((e) => e && e.statement);
}

// ---- inline SVG icons (monochrome, currentColor) ----
const svg = (children, sz = 13) => (
  <svg viewBox="0 0 24 24" width={sz} height={sz} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const Icon = {
  refresh: () => svg(<><path d="M4 4v6h6M20 20v-6h-6" /><path d="M20 10a8 8 0 0 0-14-4M4 14a8 8 0 0 0 14 4" /></>),
  left: () => svg(<path d="M15 6l-6 6 6 6" />, 12),
  right: () => svg(<path d="M9 6l6 6-6 6" />, 12),
  close: () => svg(<path d="M6 6l12 12M18 6L6 18" />, 16),
  pencil: () => svg(<><path d="M4 20h4L18 10l-4-4L4 16v4z" /><path d="M13 7l4 4" /></>, 12),
  check: () => svg(<path d="M5 13l4 4L19 7" />, 12),
  warn: () => svg(<><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16v.4" /></>, 12),
  stop: () => svg(<rect x="6" y="6" width="12" height="12" rx="1" />, 12),
  download: () => svg(<><path d="M12 3v12" /><path d="M7 11l5 5 5-5" /><path d="M5 21h14" /></>, 12),
  plus: () => svg(<path d="M12 5v14M5 12h14" />, 13),
  copy: () => svg(<><rect x="9" y="9" width="11" height="11" rx="1.5" /><path d="M5 15V5a1 1 0 0 1 1-1h10" /></>, 12),
};

// ---------- small presentational pieces ----------

const StatusChip = ({ status, map }) => {
  const s = map[status] || { label: status || '—', kind: 'muted' };
  return <span className={`pd-chip pd-chip--${s.kind}`}><span className="pd-dot" />{s.label}</span>;
};

// A failed turn returns [message, diagnostic block]: the message is one line,
// the block is multi-line (stderr, CLI control lines, partial output). Show the
// messages plainly and fold the blocks away — they are what makes an upstream
// failure debuggable, so they stay one click from the error, not in a log file.
const ErrorList = ({ errors }) => {
  const list = (errors || []).filter(Boolean).map(String);
  const msgs = list.filter((e) => !e.includes('\n'));
  const details = list.filter((e) => e.includes('\n'));
  return (
    <>
      {msgs.length > 0 && <ul className="pd-errlist">{msgs.map((e, i) => <li key={i}>{e}</li>)}</ul>}
      {details.map((d, i) => (
        <details className="pd-fold" key={i}>
          <summary className="pd-danger">Failure details (stderr, CLI output)</summary>
          <div className="pd-pre pd-mono" style={{ maxHeight: 260, overflow: 'auto' }}>{d}</div>
        </details>
      ))}
    </>
  );
};

// The tab names the agent, its prompt version and the model behind it. The
// model comes from the loop record when the turn already ran; otherwise it
// falls back to what the server would use right now (registry key == role).
const AgentTab = ({ role, model, version, onClick }) => {
  const models = useModels();
  const id = model || modelKey(models, role);
  return (
    <button type="button" className="pd-agenttab" onClick={onClick} title={id ? `View this agent's prompt · model ${id}` : "View this agent's prompt"}>
      {ROLE[role] ? ROLE[role].label : role}
      {version && <span className="pd-ver">{version}</span>}
      {id && <span className="pd-ver pd-ver--model">{shortModel(id)}</span>}
    </button>
  );
};

// Researcher variants: every facts pass is one 'tres' message of the task
// exchange, each carrying the focus (topic) it was gathered with. The one in
// view is the exchange's active_research_id (else the newest); the bubble shows
// a ◀ n / N ▶ switcher over them — nothing is ever overwritten.
const researchVariants = (taskLoop) => ((taskLoop && taskLoop.messages) || []).filter((m) => m && m.role === 'tres' && m.body);
const activeResearch = (taskLoop) => {
  const v = researchVariants(taskLoop);
  if (!v.length) return null;
  const id = taskLoop && taskLoop.active_research_id;
  return (id && v.find((m) => m.id === id)) || v[v.length - 1];
};

const BranchSwitcher = ({ meta, onSwitch, disabled }) => {
  if (!meta || meta.count <= 1) return null;
  const go = (d) => {
    const n = (meta.index + d + meta.count) % meta.count;
    onSwitch(meta.sibling_ids[n]);
  };
  return (
    <span className="pd-branch" title="Switch between regenerated variants of this turn">
      <button type="button" onClick={() => go(-1)} disabled={disabled} aria-label="Previous variant">{Icon.left()}</button>
      <span className="pd-branch-count">{meta.index + 1} / {meta.count}</span>
      <button type="button" onClick={() => go(1)} disabled={disabled} aria-label="Next variant">{Icon.right()}</button>
    </span>
  );
};

const Verdict = ({ kind, children }) => (
  <span className={`pd-verdict pd-verdict--${kind}`}>{kind === 'ok' ? Icon.check() : Icon.warn()}{children}</span>
);

// Colour of a fact's status chip from its (Russian, model-authored) label.
const factStatusKind = (status) => {
  const s = (status || '').toUpperCase();
  if (s.startsWith('ПОДТВЕР')) return 'ok';       // confirmed
  if (s.startsWith('ОПРОВЕР')) return 'bad';       // refuted
  return 'unknown';                                 // "не подтверждён" / anything else
};
// A shape per status so it reads without relying on hue (colour-blind safe):
// the chip colour is a secondary cue on top of the icon.
const FACT_ST_ICON = { ok: '✓', bad: '✗', unknown: '?' };

// The URL the checker recorded as the confirmation/refutation source for a fact.
// Rendered as a plain link (a bare "источник ↗" chip so long URLs don't wrap the
// row); non-http values are shown as text rather than a dead link.
const SOURCE_PLACEHOLDER_RE = /^(?:[—–\-]+|нет|н\/?д|n\/?a|none|null)$/i;
const FactSource = ({ source }) => {
  const s = String(source || '').trim();
  if (!s || SOURCE_PLACEHOLDER_RE.test(s)) return null; // dropped line, dash, "нет" → no source
  if (/^https?:\/\//i.test(s)) {
    return <div className="pd-fact-src"><a href={s} target="_blank" rel="noreferrer noopener" title={s}>источник ↗</a></div>;
  }
  return <div className="pd-fact-src pd-fact-src--plain">{s}</div>;
};

// The fact-checker's running list of claims, shown IN FULL each round with this
// round's diff highlighted in place: facts new this round are tinted, facts
// dropped this round are struck through (still visible). The raw ledger markup
// and the ADD/REMOVE edit commands are deliberately not rendered.
//   ledger_entries — the list AFTER this round (id, statement, status, source, note);
//   prior_entries  — the list the round started from, same shape (backfilled by
//                    migration on old messages);
//   ledger_ops == null — this was the first ledger ever (nothing before it), so
//                    there is no diff: show every row plain, not "all new".
const FactLedger = ({ msg }) => {
  const current = Array.isArray(msg.ledger_entries) ? msg.ledger_entries : [];
  if (!current.length) {
    // Legacy message with only the raw text (or nothing) — degrade gracefully.
    return msg.ledger
      ? (<div className="pd-facts"><div className="pd-microlabel">Facts</div><div className="pd-ledger">{msg.ledger}</div></div>)
      : null;
  }
  const prior = Array.isArray(msg.prior_entries) ? msg.prior_entries : [];
  const baseline = msg.ledger_ops == null;
  const priorIds = new Set(prior.map((e) => e.id));
  const currentIds = new Set(current.map((e) => e.id));
  const rows = current.map((e) => ({ ...e, state: baseline || priorIds.has(e.id) ? 'same' : 'add' }));
  if (!baseline) {
    for (const e of prior) if (!currentIds.has(e.id)) rows.push({ ...e, state: 'del' });
  }
  rows.sort((a, b) => (a.id || 0) - (b.id || 0));
  return (
    <div className="pd-facts">
      <div className="pd-microlabel">Facts · {current.length}</div>
      {rows.map((r) => {
        const sk = factStatusKind(r.status);
        return (
          <div key={`${r.state}-${r.id}`} className={`pd-fact pd-fact--${r.state}`}>
            <span className="pd-fact-id">F{r.id}{r.state === 'add' && <span className="pd-fact-new">NEW</span>}</span>
            <span className={`pd-fact-st pd-fact-st--${sk}`}>
              <span className="pd-fact-ico" aria-hidden="true">{FACT_ST_ICON[sk]}</span>{r.status || '—'}
            </span>
            <div className="pd-fact-main">
              <div className="pd-fact-txt">{r.statement}</div>
              {r.note ? <div className="pd-fact-note">{r.note}</div> : null}
              <FactSource source={r.source} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// The live counterpart of FactLedger: fact rows parsed straight from the
// streaming answer, so they appear as the checker writes them. No round diff
// (that needs the finished ledger + prior list) — freshly ADDed facts have no
// id yet and just carry a NEW tag; a partly-streamed status shows as "…".
const LiveFactLedger = ({ entries }) => {
  if (!entries || !entries.length) return null;
  return (
    <div className="pd-facts">
      <div className="pd-microlabel">Facts · {entries.length}</div>
      {entries.map((r, i) => {
        const sk = factStatusKind(r.status);
        return (
          <div key={r.id != null ? `F${r.id}` : `add-${i}`} className={`pd-fact pd-fact--${r.isNew ? 'add' : 'same'}`}>
            <span className="pd-fact-id">{r.id != null ? `F${r.id}` : ''}{r.isNew && <span className="pd-fact-new">NEW</span>}</span>
            <span className={`pd-fact-st pd-fact-st--${sk}`}>
              <span className="pd-fact-ico" aria-hidden="true">{FACT_ST_ICON[sk]}</span>{r.status || '…'}
            </span>
            <div className="pd-fact-main">
              <div className="pd-fact-txt">{r.statement}</div>
              {r.note ? <div className="pd-fact-note">{r.note}</div> : null}
              <FactSource source={r.source} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---- critic structured output (procedural critic, stage2crit v2) ----
// The critic ends its answer with one fenced ```json block
//   { verdict, summary, blockers[], optional[] }   items: {type, where, what}
// A FINISHED message already carries these fields split out by the server
// (parseCriticResult): m.summary / m.blockers / m.optional — no parsing here.
// While STREAMING we only have the raw text, so parse the (possibly still open)
// json block leniently so findings appear the moment each one is written.

// Pull one string field out of an object substring; tolerates an unterminated
// value (the closing quote may not have streamed in yet).
const critField = (objText, field) => {
  const m = String(objText).match(new RegExp(`"${field}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"?`));
  return m ? m[1].replace(/\\"/g, '"').replace(/\\n/g, ' ').replace(/\\\\/g, '\\').trim() : '';
};

// Scan the `blockers`/`optional` array of a partial json body into item objects.
// A proper brace/string state machine so a `]`/`}` inside a "what" string does
// not close the array early; a trailing half-written object is included too (it
// just gets dropped until its "what" appears).
function extractCriticItems(body, key) {
  const start = new RegExp(`"${key}"\\s*:\\s*\\[`).exec(body);
  if (!start) return [];
  const objs = [];
  let depth = 0, inStr = false, esc = false, objStart = -1;
  for (let i = start.index + start[0].length; i < body.length; i++) {
    const ch = body[i];
    if (inStr) {
      if (esc) esc = false; else if (ch === '\\') esc = true; else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') { inStr = true; continue; }
    if (ch === '{') { if (depth === 0) objStart = i; depth++; continue; }
    if (ch === '}') { depth--; if (depth === 0 && objStart >= 0) { objs.push(body.slice(objStart, i + 1)); objStart = -1; } continue; }
    if (ch === ']' && depth === 0) break; // array closed
  }
  if (objStart >= 0) objs.push(body.slice(objStart)); // trailing half-streamed object
  return objs
    .map((o) => ({ type: critField(o, 'type'), where: critField(o, 'where'), what: critField(o, 'what') }))
    .filter((x) => x.what);
}

// Live parse of the critic's streaming answer → { summary, blockers, optional, mechanical }.
// The machine block streams LAST (after the human prose), so this stays empty
// until the ```json fence arrives, then fills field-by-field.
function parseCriticLiveText(text) {
  const s = String(text || '');
  const fence = s.search(/```json/i);
  if (fence < 0) return { summary: '', blockers: [], optional: [], mechanical: [] };
  let body = s.slice(fence + s.slice(fence).indexOf('json') + 4);
  const close = body.indexOf('```');
  if (close >= 0) body = body.slice(0, close);
  return { summary: critField(body, 'summary'), blockers: extractCriticItems(body, 'blockers'), optional: extractCriticItems(body, 'optional'), mechanical: extractCriticItems(body, 'mechanical') };
}

// The prose part of the critic's answer for live display: everything BEFORE the
// machine json block, with any trailing ВЕРДИКТ: line dropped (the verdict shows
// as a chip in the bubble head, not in the body).
function criticProse(text) {
  let s = String(text || '');
  const fence = s.search(/```json/i);
  if (fence >= 0) s = s.slice(0, fence);
  return s.replace(/^\s*ВЕРДИКТ\s*:.*$/gim, '').trim();
}

// Shape per finding kind — a distinct icon + word label so severity reads
// without relying on hue (colour-blind safe); the tint is only a second cue.
const CRIT_KIND = {
  blocker: { label: 'blocker', ico: '✗', cls: 'bad' },
  optional: { label: 'optional', ico: '○', cls: 'unknown' },
  // Punctuation-level findings (prompt v21): never gate, never reach the
  // writer — the formalizer applies them to the final text.
  mechanical: { label: 'mechanical', ico: '⌁', cls: 'unknown' },
};

// The critic's findings, rendered like the fact-checker's ledger: a summary
// paragraph then one card per blocker (must-fix), optional (nice-to-have) and
// mechanical (applied by the formalizer, not the writer), in that order. Used
// both for a finished message (fields off the server) and, while streaming, for
// the partially-parsed block — same look either way.
const CriticFindings = ({ summary, blockers, optional, mechanical }) => {
  const bl = (Array.isArray(blockers) ? blockers : []).filter((x) => x && x.what);
  const op = (Array.isArray(optional) ? optional : []).filter((x) => x && x.what);
  const me = (Array.isArray(mechanical) ? mechanical : []).filter((x) => x && x.what);
  if (!summary && !bl.length && !op.length && !me.length) return null;
  const rows = [...bl.map((it) => ({ ...it, kind: 'blocker' })), ...op.map((it) => ({ ...it, kind: 'optional' })), ...me.map((it) => ({ ...it, kind: 'mechanical' }))];
  return (
    <div className="pd-facts pd-crit">
      {summary ? <div className="pd-crit-sum">{summary}</div> : null}
      {rows.length > 0 && (
        <>
          <div className="pd-microlabel">Findings · {bl.length} blocking{op.length ? ` · ${op.length} optional` : ''}{me.length ? ` · ${me.length} mechanical (fixed by the formalizer)` : ''}</div>
          {rows.map((r, i) => {
            const k = CRIT_KIND[r.kind];
            return (
              <div key={i} className={`pd-fact pd-critrow pd-critrow--${r.kind}`}>
                <span className={`pd-fact-st pd-fact-st--${k.cls}`}>
                  <span className="pd-fact-ico" aria-hidden="true">{k.ico}</span>{k.label}
                </span>
                <div className="pd-fact-main">
                  <div className="pd-fact-txt">{r.what}</div>
                  {(r.where || r.type) ? (
                    <div className="pd-fact-note">{r.type ? <span className="pd-crit-type">{r.type}</span> : null}{r.where}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

const CandidateCard = ({ cand, chosen, busy, onChoose, onGate, onRedo, onSwitch, onEdit, onDelete, live, branch }) => {
  // A candidate holds versions after a redo; show the active one. Absent → the
  // candidate itself is the single version.
  const versions = Array.isArray(cand.versions) && cand.versions.length ? cand.versions : null;
  const active = versions ? Math.min(Math.max(cand.active || 0, 0), versions.length - 1) : 0;
  const av = versions ? versions[active] : cand;
  const typeKind = TYPE_KIND[av.type] || 'muted';
  const gate = av.gate || null;
  const killed = !!(gate && gate.verdict === 'kill');
  const items = (gate && gate.checklist && Array.isArray(gate.checklist.items)) ? gate.checklist.items : [];
  const failed = items.filter((it) => it && !it.ok);

  // In-place editing: the card's own fields turn into inputs (no separate popup).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const startEdit = () => { setDraft({ question: av.question || '', answer: av.answer || '', type: av.type || '', payoff: av.payoff || '' }); setEditing(true); };
  const saveEdit = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) return;
    await onEdit(cand.num, {
      question: draft.question.trim(), answer: draft.answer.trim(),
      type: draft.type.trim() || undefined, payoff: draft.payoff.trim() || undefined,
    });
    setEditing(false);
  };

  return (
    <div className={`pd-card${chosen ? ' pd-card--chosen' : ''}${killed ? ' pd-card--killed' : ''}${live ? ' pd-card--live' : ''}`}>
      <div className="pd-card-head">
        <span className="pd-card-num">{cand.manual ? '✍ my card' : `№${cand.num}`}</span>
        {cand.id && <span className="pd-cardid" title={cand.id}>{cand.id.slice(0, 8)}</span>}
        {!editing && av.type && <span className={`pd-tag pd-tag--${typeKind}`}>{av.type}</span>}
        {gate && (killed
          ? <span className="pd-tag pd-tag--danger">gate: killed</span>
          : <span className="pd-tag pd-tag--ghost-ok">gate: passed</span>)}
        {branch && (
          <span className={`pd-tag ${branch.approved ? 'pd-tag--ok' : 'pd-tag--accent'}`}
            title={`This card already has a tail — ${branch.status ? 'a write phase' : 'checker verdict(s)'} in its branch; Choose it to switch there${branch.active ? ' (active now)' : ''}`}>
            {branch.approved ? 'story ✓' : (branch.status ? 'story' : '✓ checked')}{branch.active ? ' · active' : ''}
          </span>
        )}
        {versions && versions.length > 1 && (
          <span className="pd-ver">
            <button type="button" className="pd-ver-btn" disabled={busy || live || active === 0} onClick={() => onSwitch && onSwitch(cand.num, active - 1)} aria-label="Previous version">‹</button>
            <span className="pd-ver-n">v{active + 1}/{versions.length}{active === 0 ? ' · orig' : ''}</span>
            <button type="button" className="pd-ver-btn" disabled={busy || live || active === versions.length - 1} onClick={() => onSwitch && onSwitch(cand.num, active + 1)} aria-label="Next version">›</button>
          </span>
        )}
        {chosen && <span className="pd-card-chosen">chosen</span>}
      </div>

      {editing ? (
        <div className="pd-cardedit">
          <textarea className="pd-textarea" style={{ minHeight: 46 }} autoFocus placeholder="The riddle"
            value={draft.question} onChange={(e) => setDraft((s) => ({ ...s, question: e.target.value }))} />
          <textarea className="pd-textarea" style={{ minHeight: 32 }} placeholder="Answer — 1–2 words; equivalent spellings split by ;"
            value={draft.answer} onChange={(e) => setDraft((s) => ({ ...s, answer: e.target.value }))} />
          <div className="pd-typepills">
            {['НА МЕСТЕ', 'ЭРУДИЦИЯ', 'ЛОГИКА'].map((t) => (
              <button key={t} type="button" className={`pd-pill${draft.type === t ? ' pd-pill--on' : ''}`}
                onClick={() => setDraft((s) => ({ ...s, type: s.type === t ? '' : t }))}>{t}</button>
            ))}
          </div>
          <textarea className="pd-textarea" style={{ minHeight: 32 }} placeholder="Payoff — optional, shown after the answer"
            value={draft.payoff} onChange={(e) => setDraft((s) => ({ ...s, payoff: e.target.value }))} />
          <div className="pd-card-actions">
            <button disabled={busy || !draft.question.trim() || !draft.answer.trim()} onClick={saveEdit} className="pd-btn pd-btn--sm pd-btn--ok">Save</button>
            <button disabled={busy} onClick={() => setEditing(false)} className="pd-btn pd-btn--sm pd-btn--ghost">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {av.question && <div className="pd-card-q">{av.question}</div>}
          {(av.answer || av.payoff) && (
            <details className="pd-fold-sm">
              <summary>answer + payoff</summary>
              {av.answer && <div className="pd-card-answer">{av.answer}</div>}
              {av.payoff && <div className="pd-card-payoff"><span className="pd-microlabel">payoff · </span>{av.payoff}</div>}
            </details>
          )}
          {/* Gate output sits BELOW the card — it happens after the card is written. */}
          {killed && gate.flags && gate.flags.length > 0 && (
            <div className="pd-card-flags">{gate.flags.join(' · ')}</div>
          )}
          {items.length > 0 && (
            <details className="pd-gatenotes" open={killed}>
              <summary>gate breakdown · {items.filter((it) => it.ok).length}/{items.length} ok</summary>
              {items.map((it, i) => (
                <div key={i} className={`pd-gatenote pd-gatenote--${it.ok ? 'ok' : 'bad'}`}>
                  <div className="pd-gatenote-t"><span className="pd-gatenote-m">{it.ok ? '✓' : '✗'}</span> {it.title || `item ${i + 1}`}</div>
                  {it.note && <div className="pd-gatenote-b">{it.note}</div>}
                </div>
              ))}
            </details>
          )}
          {!live && (
            <div className="pd-card-actions">
              <button disabled={busy} onClick={() => onChoose(cand)} className={`pd-btn pd-btn--sm ${chosen ? 'pd-btn--ghost' : 'pd-btn--ok'}`}>
                {chosen ? 'Re-pick' : 'Choose'}
              </button>
              {onEdit && <button disabled={busy} onClick={startEdit} className="pd-btn pd-btn--sm pd-btn--ghost">Edit</button>}
              {onDelete && <button disabled={busy} onClick={() => onDelete(cand.num)} className="pd-btn pd-btn--sm pd-btn--ghost pd-btn--danger" title="Delete this card">Delete</button>}
              {(onGate || (onRedo && failed.length > 0)) && <span className="pd-card-actions-sep" />}
              {onGate && (
                <button disabled={busy} onClick={() => onGate(cand.num)} className="pd-btn pd-btn--sm pd-btn--check" title="Checklist agent — evaluate this card against the criteria">
                  {gate ? 'Re-run checklist' : 'Run checklist'}
                </button>
              )}
              {onRedo && failed.length > 0 && (
                <button disabled={busy} onClick={() => onRedo(cand.num)} className="pd-btn pd-btn--sm pd-btn--gen" title="Generator agent — regenerate this card from the gate's flagged notes">
                  Send to generator
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DraftView = ({ draft, defaultOpen }) => {
  const v = draft || {};
  const noTask = !!(v.is_no_task != null ? v.is_no_task
    : v.no_task != null ? v.no_task
    : v.is_view_only != null ? v.is_view_only : v.view_only);
  const field = (label, val) => (val ? (
    <div className="pd-draft-field">
      <div className="pd-microlabel">{label}</div>
      <div className="pd-pre">{val}</div>
    </div>
  ) : null);
  return (
    <details className="pd-fold" open={defaultOpen}>
      <summary>location_draft</summary>
      <div className="pd-draft">
        {field('Description', v.description)}
        {!noTask && field('Task', v.task)}
        {!noTask && field('Answer', v.answer)}
        {field('Hint', v.hint)}
        {!noTask && field('After message', v.after_message)}
        {noTask && <div className="pd-muted">No-task point (view only).</div>}
      </div>
    </details>
  );
};

const ReasoningView = ({ thinking, tools }) => {
  if (!thinking && (!tools || tools.length === 0)) return null;
  return (
    <details className="pd-reason">
      <summary>reasoning</summary>
      <div className="pd-reason-body">
        {(tools || []).map((tl, i) => <div key={i} className="pd-tool">{tl}</div>)}
        {thinking}
      </div>
    </details>
  );
};

const SegmentsView = ({ segments, streaming, hideText }) => {
  const groups = [];
  (segments || []).forEach((s) => {
    if (s.t === 'text') {
      if (!hideText && s.text.trim()) groups.push({ kind: 'text', text: s.text });
    } else {
      const last = groups[groups.length - 1];
      if (last && last.kind === 'reasoning') last.items.push(s);
      else groups.push({ kind: 'reasoning', items: [s] });
    }
  });
  return (
    <>
      {groups.map((g, i) => (g.kind === 'text' ? (
        <Md key={i}>{g.text}</Md>
      ) : (
        <details key={i} className="pd-reason" {...(streaming && i === groups.length - 1 ? { open: true } : {})}>
          <summary>reasoning{streaming && i === groups.length - 1 ? '…' : ''}</summary>
          <div className="pd-reason-body">
            {g.items.map((s, j) => (s.t === 'tool'
              ? <div key={j} className="pd-tool">{s.text}</div>
              : <span key={j}>{s.text}</span>))}
          </div>
        </details>
      )))}
    </>
  );
};

const WORD_TOKEN_RE = /\s+|[\p{L}\p{N}]+|[^\s\p{L}\p{N}]+/gu;
const SENTENCE_TOKEN_RE = /[^.!?…]*[.!?…]+["»)\]]*\s*|[^.!?…]+$/gu;
const tokenize = (s, re) => (s || '').match(re) || [];
const DiffView = ({ oldText, newText, mode }) => {
  const parts = mode === 'chars'
    ? diffChars(oldText || '', newText || '')
    : diffArrays(tokenize(oldText, mode === 'sentences' ? SENTENCE_TOKEN_RE : WORD_TOKEN_RE),
                 tokenize(newText, mode === 'sentences' ? SENTENCE_TOKEN_RE : WORD_TOKEN_RE));
  return (
    <div className="pd-pre" style={{ marginTop: 4 }}>
      {parts.map((p, i) => {
        const text = Array.isArray(p.value) ? p.value.join('') : p.value;
        if (p.added) return <span key={i} className="pd-diff-add">{text}</span>;
        if (p.removed) return <span key={i} className="pd-diff-del">{text}</span>;
        return <span key={i}>{text}</span>;
      })}
    </div>
  );
};

// startedAt — server timestamp of the 'turn' event; ended — the server's turn_end
// marker landed (the turn is over, its stored message is about to arrive).
const blankTurn = () => ({ role: null, round: null, num: null, segs: [], status: null, startedAt: null, ended: false });

const FIELD_PLACEHOLDER = {
  brief: "Note for the Author's brief — folded in when you press Write…",
  writer: "To the Author (as if from the Critic): triggers a full rewrite. The Critic never sees this…",
};
const FIELD_BTN = { brief: 'Save note', writer: 'Send → Author rewrites' };

const MODELS = ['claude-opus-5', 'claude-opus-4-6', 'claude-opus-4-8', 'claude-sonnet-5', 'claude-haiku-4-5-20251001'];

// Kind -> badge label for the Context diagram (colours live in PD_CSS).
const CX_KIND = {
  self: 'own memory',
  agent: 'other agent',
  human: 'human',
  input: 'static input',
};

// "What this agent sees" — a small diagram derived from the server's
// context-spec (session mode + template-derived input list + non-template
// extras). Fetched once; state-independent. See auto-pipeline/context_spec.js.
const ContextDiagram = ({ role }) => {
  const [spec, setSpec] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    fetch('/api/pipeline/context-spec', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (live) { if (d && d.ok) setSpec(d.spec); else setErr(true); } })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, []);
  if (err) return null;
  const d = spec && spec[role];
  if (!d) return <div className="pd-cx pd-cx--loading">loading context…</div>;

  const chip = (item, i, extra) => (
    <div key={`${extra ? 'x' : 'i'}${i}`} className={`pd-cx-src pd-cx-src--${item.kind}${extra ? ' pd-cx-src--extra' : ''}`}>
      <span className="pd-cx-dot" />
      <span className="pd-cx-srctext">
        <span className="pd-cx-label">{item.label}</span>
        {item.key && <span className="pd-cx-key">{`{{${item.key}}}`}</span>}
        {extra && <span className="pd-cx-key pd-cx-key--extra">arrives as a later turn</span>}
      </span>
    </div>
  );

  return (
    <div className="pd-cx">
      <div className={`pd-cx-session pd-cx-session--${d.session.mode}`}>
        <span className="pd-cx-sbadge">{d.session.mode === 'resumed' ? '↻ remembers its own turns' : '○ fresh call'}</span>
        <span className="pd-cx-snote">{d.session.note}</span>
      </div>
      <div className="pd-cx-flow">
        <div className="pd-cx-sources">
          {d.inputs.map((it, i) => chip(it, i, false))}
          {d.extras.map((it, i) => chip(it, i, true))}
          {d.inputs.length === 0 && d.extras.length === 0 && <div className="pd-cx-empty">no inputs</div>}
        </div>
        <div className="pd-cx-arrow" aria-hidden>→</div>
        <div className="pd-cx-agent">{d.label}</div>
      </div>
      <div className="pd-cx-legend">
        {['self', 'agent', 'human', 'input'].map((k) => (
          <span key={k} className={`pd-cx-leg pd-cx-src--${k}`}><span className="pd-cx-dot" />{CX_KIND[k]}</span>
        ))}
      </div>
    </div>
  );
};

// The prompt of one agent (opened from the status-bar tabs), read-only. Agents
// carry their role in the SYSTEM prompt and their turn input in the USER template;
// the panel shows SYSTEM first, then the USER template below it. It reads the
// snapshot captured at run time (so an old run keeps the prompt it actually used).
// When there is NO captured prompt text — either the agent hasn't run on this
// point yet (opened as a preview), or the run is old enough to predate prompt
// capture — the panel falls back to the CURRENT template read live from the .md,
// clearly labelled as current so it is never mistaken for a saved snapshot.
const NODE_OF_ROLE = {
  tres: '2tres', tgen: '2tgen', tcheck: '2tcheck', tfact: '2tfact',
  writer: '2brief', critic: '2crit', factcheck: '2fact', formal: '2form',
};
const AgentPromptView = ({ role, writeLoop, taskLoop, onClose }) => {
  const p = promptOf(writeLoop, taskLoop, role);
  const model = modelOf(writeLoop, taskLoop, role);
  const snapSys = (p && p.system && p.system.trim()) ? p.system : '';
  const snapUsr = (p && p.user && p.user.trim()) ? p.user : '';
  const captured = !!(snapSys || snapUsr);
  const node = NODE_OF_ROLE[role];
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (captured || !node) return undefined;
    let alive = true;
    fetch(`/api/pipeline/prompt-template/${node}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setLive(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [captured, node]);
  const usingLive = !captured && !!live;
  const sys = captured ? snapSys : (live && live.system && live.system.trim() ? live.system : '');
  const usr = captured ? snapUsr : (live && live.user && live.user.trim() ? live.user : '');
  const title = sys ? 'SYSTEM prompt' : usr ? 'prompt' : 'SYSTEM prompt';
  const ver = captured ? promptVer(p) : (live && live.version ? `v${live.version}` : null);
  return (
    <div className="pd-overlay pd-overlay--top" onMouseDown={onClose}>
      <div className="pd-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pd-panel-head">
          <h3>{ROLE[role] ? ROLE[role].label : role} — {title} {ver && <span className="pd-tag pd-tag--ghost">{ver}</span>}</h3>
          <button className="pd-iconbtn" onClick={onClose} aria-label="Close">{Icon.close()}</button>
        </div>
        <div className="pd-panel-sub">model {model || '—'}</div>
        <div className="pd-panel-body">
          <div className="pd-cx-wrap"><div className="pd-cx-title">Context — what this agent is fed</div><ContextDiagram role={role} /></div>
          {usingLive && <div className="pd-cx-title" style={{ marginTop: 4 }}>No saved prompt snapshot — showing the current template</div>}
          {sys && <div className="pd-codeview">{sys}</div>}
          {usr && <>
            {sys && <div className="pd-cx-title" style={{ marginTop: 4 }}>User message (template)</div>}
            <div className="pd-codeview">{usr}</div>
          </>}
          {!sys && !usr && <div className="pd-codeview">{!captured && node ? 'Loading current template…' : '(prompt text was not captured)'}</div>}
        </div>
      </div>
    </div>
  );
};

// Regenerate one turn: fetches its reconstructed system+user, lets you edit and
// fork. mode 'view' opens read-only with an "Edit & regenerate" step.
const RegenerateModal = ({ questId, uuid, node, mode, busy, onClose, onRegenerate }) => {
  const [err, setErr] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [sys, setSys] = useState('');
  const [usr, setUsr] = useState('');
  const [model, setModel] = useState('');
  const [editing, setEditing] = useState(mode === 'edit');
  const [meta, setMeta] = useState({ role: node.role, round: node.round });
  // The prompt this turn actually ran with (snapshot) and the current version from
  // the file (fresh); the toggle swaps which one fills the editable fields.
  const [snap, setSnap] = useState(null);
  const [fresh, setFresh] = useState(null);
  const [useFresh, setUseFresh] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`/api/pipeline/quest/${questId}/location/${uuid}/node/${node.id}/prompt`, { credentials: 'include' })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!live) return;
        if (!ok) { setErr((d && d.error) || 'Failed to load the prompt'); setLoaded(true); return; }
        const snapObj = { system: d.system || '', user: d.user || '', version: d.version };
        const freshObj = d.fresh || null;
        setSnap(snapObj);
        setFresh(freshObj);
        // Regenerating defaults to the LATEST file version; the read-only view
        // shows the snapshot the turn actually ran with.
        const startFresh = mode === 'edit' && !!freshObj;
        const src = startFresh ? freshObj : snapObj;
        setSys(src.system || ''); setUsr(src.user || '');
        setUseFresh(startFresh);
        setModel(d.model || MODELS[0]);
        setMeta({ role: d.role, round: d.round });
        setLoaded(true);
      })
      .catch((e) => { if (live) { setErr(e.message); setLoaded(true); } });
    return () => { live = false; };
  }, [questId, uuid, node.id]);

  // Load the snapshot or the fresh-from-file prompt into the editable fields.
  const chooseSource = (wantFresh) => {
    const src = wantFresh ? fresh : snap;
    if (src) { setSys(src.system || ''); setUsr(src.user || ''); }
    setUseFresh(wantFresh);
  };
  const freshDiffers = !!(fresh && snap && (fresh.version !== snap.version || fresh.system !== snap.system || fresh.user !== snap.user));

  const ro = !editing;
  const roleLabel = ROLE[meta.role] ? ROLE[meta.role].label : meta.role;
  return (
    <div className="pd-overlay pd-overlay--top" onMouseDown={busy ? undefined : onClose}>
      <div className="pd-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pd-panel-head">
          <h3>{editing ? 'Regenerate turn' : 'Prompt'} <span className="pd-tag pd-tag--role">{roleLabel}{meta.round != null ? ` · round ${meta.round}` : ''}</span></h3>
          <button className="pd-iconbtn" onClick={onClose} aria-label="Close">{Icon.close()}</button>
        </div>
        <div className="pd-panel-sub">
          {editing
            ? 'Edit the prompts this turn ran with, then generate an alternative. It lands as a new branch.'
            : 'Exactly what this turn ran with — read-only.'}
        </div>
        {err && <div className="pd-error">{err}</div>}
        <div className="pd-panel-body">
          {editing && freshDiffers && (
            <label className="pd-freshtoggle">
              <input type="checkbox" checked={useFresh} onChange={(e) => chooseSource(e.target.checked)} />
              <span>Use latest prompt from file</span>
              <span className="pd-hint">ran v{snap.version != null ? snap.version : '?'} → file v{fresh.version != null ? fresh.version : '?'}</span>
            </label>
          )}
          <div className="pd-field">
            <label>System prompt <span className="pd-hint">the agent's role</span></label>
            <textarea className="pd-textarea pd-mono" style={{ minHeight: 120 }} value={sys} readOnly={ro}
              spellCheck={false} onChange={(e) => setSys(e.target.value)} placeholder={loaded ? '' : 'loading…'} />
          </div>
          <div className="pd-field">
            <label>User prompt <span className="pd-hint">this turn's input, reconstructed</span></label>
            <textarea className="pd-textarea pd-mono" style={{ minHeight: 140 }} value={usr} readOnly={ro}
              spellCheck={false} onChange={(e) => setUsr(e.target.value)} placeholder={loaded ? '' : 'loading…'} />
          </div>
        </div>
        <div className="pd-panel-foot">
          <div className="pd-modelpick">
            <label>Model</label>
            <select value={model} disabled={ro} onChange={(e) => setModel(e.target.value)}>
              {MODELS.concat(MODELS.includes(model) || !model ? [] : [model]).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <span className="pd-spacer" />
          {ro ? (
            <>
              <button className="pd-btn" onClick={onClose}>Close</button>
              <button className="pd-btn pd-btn--primary" onClick={() => { setEditing(true); if (fresh) chooseSource(true); }} disabled={!loaded}>Edit &amp; regenerate</button>
            </>
          ) : (
            <>
              <button className="pd-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="pd-btn" disabled={busy || !sys.trim()} onClick={() => onRegenerate(node.id, node.role, sys, usr, model)}>
                {busy ? 'Regenerating…' : 'Regenerate'}
              </button>
              {meta.role === 'writer' && (
                <button className="pd-btn pd-btn--primary" disabled={busy || !sys.trim()}
                  title="Regenerate this Author version, then run the review → rewrite loop automatically to the end — until the Critic approves (or the round cap), then formalize into quest fields."
                  onClick={() => onRegenerate(node.id, node.role, sys, usr, model, true)}>
                  {busy ? 'Starting…' : 'Regenerate & run to end'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// A hand-written Author turn: 'replace' lands as a sibling variant of an
// existing turn (the original stays switchable, like a regeneration); 'append'
// becomes the Author's next message after the tip review/note. Plain textarea,
// no model call — the text is stored verbatim as the story.
const ManualTurnModal = ({ mode, initial, busy, onClose, onSave }) => {
  const [text, setText] = useState(initial || '');
  return (
    <div className="pd-overlay pd-overlay--top" onMouseDown={busy ? undefined : onClose}>
      <div className="pd-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pd-panel-head">
          <h3>{mode === 'replace' ? 'Your version of this turn' : 'Write the next Author turn yourself'} <span className="pd-tag pd-tag--role">Author</span></h3>
          <button className="pd-iconbtn" onClick={onClose} aria-label="Close">{Icon.close()}</button>
        </div>
        <div className="pd-panel-sub">
          {mode === 'replace'
            ? 'Your text replaces the generated story as a sibling variant — the original stays and the ‹ › switcher flips between them.'
            : "Your text becomes the Author's next message on this branch — reviewers can then judge it like a generated one."}
        </div>
        <div className="pd-panel-body">
          <div className="pd-field">
            <label>Story text <span className="pd-hint">stored verbatim, no model call</span></label>
            <textarea className="pd-textarea" style={{ minHeight: 280 }} autoFocus value={text}
              onChange={(e) => setText(e.target.value)} placeholder="The full story text of this point…" />
          </div>
        </div>
        <div className="pd-panel-foot">
          <span className="pd-spacer" />
          <button className="pd-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="pd-btn pd-btn--primary" disabled={busy || !text.trim()} onClick={() => onSave(text.trim())}>
            {busy ? 'Saving…' : 'Save turn'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PointDialogModal = ({ questId, uuid, pointName, onClose, onChanged, autoScroll }) => {
  const [taskLoop, setTaskLoop] = useState(null);
  // The Focus field of the Researcher bubble. Prefilled from the variant in
  // view (switching variants re-syncs it); edited + Gather = a new variant.
  const [focusDraft, setFocusDraft] = useState('');
  const activeResId = (activeResearch(taskLoop) || {}).id || null;
  useEffect(() => {
    const a = activeResearch(taskLoop);
    setFocusDraft(a && a.focus ? a.focus : '');
  }, [activeResId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Ready-to-paste authoring brief (guide + this location & city), rendered
  // server-side from auto-pipeline/prompts/guide_task_authoring_prompt.md.
  const [guidePrompt, setGuidePrompt] = useState('');
  // Transient "Copied!" state for the guide-prompt button (the toast lives at the
  // composer, off-screen from the Researcher bubble — so the button self-confirms).
  const [guideCopied, setGuideCopied] = useState(false);
  const copyTimerRef = useRef(null);
  // Same, for the "Copy full prompt" (guide + researcher facts) button.
  const [fullCopied, setFullCopied] = useState(false);
  const fullTimerRef = useRef(null);
  const [choice, setChoice] = useState(null);
  const [writeLoop, setWriteLoop] = useState(null);
  const writeLoopRef = useRef(null); // mirrors writeLoop for the SSE closure
  const dirtyRef = useRef(false); // a generation finished → refresh the parent ON CLOSE, not mid-work
  const [runId, setRunId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  // Hand-written task card being composed: null (closed) | { question, answer, type, payoff }.
  const [manual, setManual] = useState(null);
  const [promptRole, setPromptRole] = useState(null);
  const [showModels, setShowModels] = useState(false);
  const [regen, setRegen] = useState(null); // { node, mode } | null
  // Hand-written Author turn being composed:
  //   { mode: 'replace', nodeId, body } — a sibling variant of an existing turn;
  //   { mode: 'append', body: '' }      — the Author's next message after the tip.
  const [manualTurn, setManualTurn] = useState(null);
  // Composer flag: send the writer note as ONE rewrite (pause before review).
  const [noteStep, setNoteStep] = useState(false);
  // Node id whose round feedback was just copied (transient "Copied!" swap).
  const [copiedFbId, setCopiedFbId] = useState(null);
  const fbTimerRef = useRef(null);
  const [diffMode, setDiffMode] = useState('off');
  // Optimistic placeholder: set at CLICK time (before the server responds) so
  // a bubble shows instantly; the real SSE stream takes over on its 'turn'.
  const [pending, setPending] = useState(null); // { role } | null
  // One-line notice when a requested check did NOT start (skipped/busy/...).
  const [notice, setNotice] = useState(null);
  // Decision branches (write phases per task decision) from /critic-loop.
  const [writeBranches, setWriteBranches] = useState([]);
  // Task-Checker verdicts of the ACTIVE decision branch (they travel with it).
  const [checks, setChecks] = useState([]);

  const loadSeqRef = useRef(0);
  const listRef = useRef(null);
  // autoScroll (a click on a notification tile) opens the feed at its newest
  // message instead of the top.
  const stickRef = useRef(!!autoScroll);
  const runningRef = useRef(false);
  const startedRef = useRef(false);
  const liveRef = useRef(blankTurn());
  // The fact-checker now streams alongside the critic; it gets its own live bubble
  // so the two concurrent streams don't clobber each other. Events tagged
  // role:'factcheck' land here, everything else stays on liveRef.
  const liveFactRef = useRef(blankTurn());
  const [, setLiveTick] = useState(0);
  const rafRef = useRef(null);
  const prevWriteStatusRef = useRef(null);

  const scheduleLiveRender = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setLiveTick((t) => t + 1);
      const el = listRef.current;
      if (el && stickRef.current) el.scrollTop = el.scrollHeight;
    });
  }, []);
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  const applyTraceEvent = useCallback((ev) => {
    if (!ev || !ev.t) return;
    // 'done' ends the whole point → clear both live bubbles.
    if (ev.t === 'done') { liveRef.current = blankTurn(); liveFactRef.current = blankTurn(); startedRef.current = false; setPending(null); return; }
    // Fact-checker's events (role-tagged) stream into their own bubble so they can
    // run concurrently with the critic; everything else stays on the main bubble.
    const targetRef = ev.role === 'factcheck' ? liveFactRef : liveRef;
    if (ev.t === 'turn') { targetRef.current = { ...blankTurn(), role: ev.role === 'taskgen' ? 'tgen' : ev.role, round: ev.round != null ? ev.round : null, num: ev.num != null ? ev.num : null, startedAt: ev.ts || null }; setPending(null); return; }
    // End-of-turn marker: the turn is over. Don't drop the bubble yet (its stored
    // message lands a DB write later — dropping now would blink); flag it, and
    // renderLive hides it once the stored message is in the feed. This is what
    // keeps a replayed trace from resurrecting a finished turn as "streaming…".
    if (ev.t === 'turn_end') {
      const endRole = ev.role === 'taskgen' ? 'tgen' : ev.role;
      if (endRole && targetRef.current.role === endRole) targetRef.current.ended = true;
      return;
    }
    // Missed-boundary guard: an SSE reconnect can swallow a 'turn' event, leaving
    // the bubble on a stale role — a later role-tagged delta would then glue one
    // agent's stream into another's bubble (critic text typing into the Author
    // bubble). A role mismatch means the boundary was lost: restart the bubble.
    const evRole = ev.role === 'taskgen' ? 'tgen' : ev.role;
    if (evRole && targetRef.current.role && targetRef.current.role !== evRole) {
      targetRef.current = { ...blankTurn(), role: evRole, round: ev.round != null ? ev.round : null };
    }
    const lv = targetRef.current;
    const pushSeg = (kind, text) => {
      const last = lv.segs[lv.segs.length - 1];
      if (last && last.t === kind) last.text += text;
      else lv.segs.push({ t: kind, text });
    };
    if (ev.t === 'thinking') pushSeg('thinking', ev.delta || '');
    else if (ev.t === 'text') pushSeg('text', ev.delta || '');
    else if (ev.t === 'tool') { lv.segs.push({ t: 'tool', text: `${ev.name}${ev.detail ? `: ${ev.detail}` : ''}` }); lv.status = ev.detail ? `web · ${ev.detail}` : `web · ${ev.name}`; }
    // Status notes (spawn banner, per-search notes) keep the bubble alive during
    // the silent stretch before the first content token streams in.
    else if (ev.t === 'status') { if (ev.msg) lv.status = ev.msg; }
  }, []);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    try {
      const [tr, wr] = await Promise.all([
        fetch(`/api/pipeline/quest/${questId}/location/${uuid}/task-loop`, { credentials: 'include' }),
        fetch(`/api/pipeline/quest/${questId}/location/${uuid}/critic-loop`, { credentials: 'include' }),
      ]);
      const tData = await tr.json().catch(() => null);
      const wData = await wr.json().catch(() => null);
      if (seq !== loadSeqRef.current) return;
      if (!tr.ok) throw new Error((tData && tData.error) || `HTTP ${tr.status}`);
      if (!wr.ok) throw new Error((wData && wData.error) || `HTTP ${wr.status}`);
      setTaskLoop(tData.loop);
      setGuidePrompt(tData.guidePrompt || '');
      setChoice(tData.choice);
      setWriteLoop(wData.loop);
      setWriteBranches(Array.isArray(wData.branches) ? wData.branches : []);
      setChecks(Array.isArray(wData.checks) ? wData.checks : []);
      setRunId(tData.runId || wData.runId);
      if (tData.choice && tData.choice.mode === 'task') setNotesDraft(tData.choice.notes || '');
      const ws = wData.loop ? wData.loop.status : null;
      if (prevWriteStatusRef.current === 'running' && ws && ws !== 'running') dirtyRef.current = true;
      prevWriteStatusRef.current = ws;
      setError(null);
    } catch (e) {
      if (seq === loadSeqRef.current) setError(e.message);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [questId, uuid, onChanged]);

  useEffect(() => { load(); }, [load]);

  // Liveness watchdog epoch: bumping it tears the EventSource down and builds a
  // fresh one (the server replays the slot trace on every connect, so a rebuild
  // fully restores the live bubbles).
  const [esEpoch, setEsEpoch] = useState(0);

  useEffect(() => {
    if (!runId) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    // While a loop is running its agents stream continuously, so a long silence
    // means the stream is dead — even when EventSource never says so (a sleeping
    // tab, or a 4xx on reconnect makes it give up silently and the modal freezes
    // on a stale snapshot). The watchdog rebuilds the connection and refetches.
    let lastEventAt = Date.now();
    const watchdog = setInterval(() => {
      if (runningRef.current && Date.now() - lastEventAt > 25000) {
        clearInterval(watchdog);
        setEsEpoch((n) => n + 1); // remount this effect: fresh ES + replay
        load();
      }
    }, 10000);
    es.onmessage = (e) => {
      lastEventAt = Date.now();
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if ((msg.type === 'pipeline_loop' || msg.type === 'pipeline_task_loop') && msg.uuid === uuid) {
        // Clear only the bubble whose message just landed, so a concurrent stream
        // (e.g. the fact-checker still running while the critic's message arrives)
        // keeps going. A role-less loop update clears the main bubble.
        if (msg.message && msg.message.role === 'factcheck') liveFactRef.current = blankTurn();
        else liveRef.current = blankTurn();
        scheduleLiveRender();
        // Update the write feed IN PLACE from the event's message list — no full
        // refetch (that was the per-message flicker). Fall back to load() for the
        // task loop, deletions (no messages), or before the loop is first loaded.
        if (msg.type === 'pipeline_loop' && Array.isArray(msg.messages) && writeLoopRef.current) {
          const ws = msg.loop ? msg.loop.status : null;
          setWriteLoop((prev) => (prev ? {
            ...prev,
            messages: msg.messages,
            status: ws || prev.status,
            rounds_done: msg.loop ? msg.loop.rounds_done : prev.rounds_done,
            max_rounds: msg.loop ? msg.loop.max_rounds : prev.max_rounds,
          } : prev));
          // Don't reload the parent mid-work (that was the "page reloads after the
          // formalizer" jank). Just mark dirty; the refresh happens on close.
          if (prevWriteStatusRef.current === 'running' && ws && ws !== 'running') dirtyRef.current = true;
          if (ws) prevWriteStatusRef.current = ws;
        } else {
          load();
        }
      } else if (msg.type === 'pipeline_trace') {
        if (!runningRef.current && !startedRef.current) return;
        // Parallel point slots share one SSE channel — apply only this point's
        // events (run-scoped events, e.g. a batch pass, carry no pointUuid).
        if (msg.ev && msg.ev.pointUuid && msg.ev.pointUuid !== uuid) return;
        applyTraceEvent(msg.ev);
        scheduleLiveRender();
      } else if (msg.type === 'pipeline_replay') {
        if (!runningRef.current && !startedRef.current) return;
        if (msg.pointUuid && msg.pointUuid !== uuid) return;
        liveRef.current = blankTurn();
        liveFactRef.current = blankTurn();
        (msg.trace || []).forEach(applyTraceEvent);
        scheduleLiveRender();
      }
    };
    // Events that fired during a dropped connection are lost for good (no replay
    // channel), so every REconnect refetches the loops — pushed messages the gap
    // swallowed appear, and the frozen live bubble gets cleared by the fetch.
    let connectedBefore = false;
    es.onopen = () => {
      if (connectedBefore) {
        liveRef.current = blankTurn();
        liveFactRef.current = blankTurn();
        scheduleLiveRender();
        load();
      }
      connectedBefore = true;
    };
    es.onerror = () => { /* auto-reconnects; resync happens in onopen; a dead-for-good stream is caught by the watchdog */ };
    return () => { clearInterval(watchdog); es.close(); };
  }, [runId, uuid, load, applyTraceEvent, scheduleLiveRender, esEpoch]);

  const taskRunning = !!(taskLoop && taskLoop.status === 'running');
  const writeRunning = !!(writeLoop && writeLoop.status === 'running');
  const anyRunning = taskRunning || writeRunning;
  runningRef.current = anyRunning;
  writeLoopRef.current = writeLoop;

  // Task section sorted chronologically (appends are already in order — the
  // sort guards legacy data); the Decision marker is a FORK POINT pinned
  // between the sections, and the write section keeps its branch-path order
  // (parent→child chains are chronological by construction).
  const taskMsgs = ((taskLoop && taskLoop.messages) || []).map((m, i) => ({ ...m, phase: 'task', _i: i }));
  taskMsgs.sort((a, b) => {
    const ta = Date.parse(a.created_at || '') || 0;
    const tb = Date.parse(b.created_at || '') || 0;
    return (ta && tb && ta !== tb) ? ta - tb : a._i - b._i;
  });
  // Critic and fact-checker run in parallel, so their stored order within a round
  // can be either way. Keep the append/branch order (a global sort would scramble
  // continuations, where round numbers repeat) and only swap the one adjacent pair
  // that can be out of order: fact-check immediately before critic of the SAME round.
  const orderedWriteMsgs = ((writeLoop && writeLoop.messages) || []).map((m) => ({ ...m, phase: 'write' }));
  for (let i = 0; i < orderedWriteMsgs.length - 1; i += 1) {
    const a = orderedWriteMsgs[i];
    const b = orderedWriteMsgs[i + 1];
    if (a.role === 'factcheck' && b.role === 'critic' && a.round === b.round) {
      orderedWriteMsgs[i] = b;
      orderedWriteMsgs[i + 1] = a;
    }
  }
  const messages = [
    ...taskMsgs,
    ...(choice ? [{ id: 'task-choice', role: 'choice', phase: 'task', created_at: choice.decided_at }] : []),
    ...orderedWriteMsgs,
  ];

  useEffect(() => {
    const el = listRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const base = `/quest/${questId}/location/${uuid}`;
  const act = async (fn) => {
    setError(null); setBusy(true);
    try { await fn(); } catch (e) { setError(e.message); setPending(null); } finally { setBusy(false); }
  };
  // Arm the optimistic placeholder for the turn a click is about to start.
  const armPending = (role) => { setNotice(null); setPending({ role }); };
  // Safety valve: if neither a 'turn' nor an error ever arrives (SSE hiccup),
  // don't show "starting…" forever.
  useEffect(() => {
    if (!pending) return undefined;
    const t = setTimeout(() => setPending(null), 45000);
    return () => clearTimeout(t);
  }, [pending]);
  // Why a requested Task-Checker run did not start, in one line.
  const CHECK_NOTICE = {
    skipped: 'Check skipped — this exact card text was already verified.',
    busy: 'Task-Checker not started — another generation is running. Use Re-check when it finishes.',
    no_exchange: 'Task-Checker not started — no task exchange to attach to.',
    error: 'Task-Checker failed to start — see server logs.',
  };
  const noteCheck = (check) => {
    if (check && check !== 'started' && check !== 'none') {
      setPending(null);
      setNotice(CHECK_NOTICE[check] || `Task-Checker not started (${check}).`);
    }
  };
  // Researcher (2tres): one web-search pass that collects facts + sources about
  // the place into a downloadable document — a human reference for writing cards.
  const research = () => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); armPending('tres');
    await papi(`${base}/task-research`, { focus: focusDraft.trim() });
    await load();
    stickRef.current = true;
  });
  // Bring another Researcher variant into view (server remembers the pick, so
  // the Facts batch and a reload see the same one).
  const selectResearch = (messageId) => act(async () => {
    await papi(`${base}/task-research-select`, { messageId });
    await load();
  });
  // (Task Generator + its gate are retired to Legacy — no startGen/sendComment.)
  // Download any text as a local .md file (the Researcher's facts document).
  const downloadText = useCallback((filename, text) => {
    const blob = new Blob([text || ''], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, []);
  // Copy the ready-to-paste authoring brief (guide + this location & city) to the
  // clipboard — the human pastes it into a chat assistant together with the
  // Researcher's facts to get 5 draft riddle candidates.
  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // Fallback for non-secure contexts where the async clipboard API is blocked.
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { document.execCommand('copy'); } catch (e2) { /* ignore */ }
      ta.remove();
    }
  }, []);
  const copyGuidePrompt = useCallback(async () => {
    if (!guidePrompt) { setNotice('Guide prompt is not ready yet.'); return; }
    await copyToClipboard(guidePrompt);
    setGuideCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setGuideCopied(false), 1800);
  }, [guidePrompt, copyToClipboard]);
  // Guide prompt with the Researcher's facts document spliced in as a quoted
  // block right after the intro paragraph — one paste feeds a chat assistant
  // both the task and the sources.
  const copyFullPrompt = useCallback(async () => {
    if (!guidePrompt) { setNotice('Guide prompt is not ready yet.'); return; }
    const resMsg = activeResearch(taskLoop);
    if (!resMsg) { setNotice('No Researcher facts yet — gather facts first.'); return; }
    const cut = guidePrompt.indexOf('\n\n');
    const block = `"""\n${resMsg.body}\n"""`;
    const full = cut === -1
      ? `${guidePrompt}\n\n${block}`
      : `${guidePrompt.slice(0, cut)}\n\n${block}${guidePrompt.slice(cut)}`;
    await copyToClipboard(full);
    setFullCopied(true);
    if (fullTimerRef.current) clearTimeout(fullTimerRef.current);
    fullTimerRef.current = setTimeout(() => setFullCopied(false), 1800);
  }, [guidePrompt, taskLoop, copyToClipboard]);
  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    if (fullTimerRef.current) clearTimeout(fullTimerRef.current);
    if (fbTimerRef.current) clearTimeout(fbTimerRef.current);
  }, []);
  // Copy one review round EXACTLY as the Author receives it (assembled server-
  // side: critic feedback + fact remarks), not the on-screen cards.
  const copyRoundFeedback = useCallback(async (nodeId) => {
    try {
      const r = await fetch(`/api/pipeline/quest/${questId}/location/${uuid}/node/${nodeId}/feedback`, { credentials: 'include' });
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error((d && d.error) || `HTTP ${r.status}`);
      if (!d.feedback) { setNotice('This round passed — nothing goes to the Author.'); return; }
      await copyToClipboard(d.feedback);
      setCopiedFbId(nodeId);
      if (fbTimerRef.current) clearTimeout(fbTimerRef.current);
      fbTimerRef.current = setTimeout(() => setCopiedFbId(null), 1800);
    } catch (e) {
      setError(e.message);
    }
  }, [questId, uuid, copyToClipboard]);
  // Add a hand-written card (question + answer required). It becomes a choosable
  // candidate immediately. The composer stays open with cleared fields so several
  // cards can be written in a row; "Done" closes it.
  const addManualCard = () => act(async () => {
    const q = (manual.question || '').trim();
    const a = (manual.answer || '').trim();
    if (!q || !a) throw new Error('Question and answer are required for a manual card');
    await papi(`${base}/task-manual`, {
      question: q, answer: a,
      type: (manual.type || '').trim() || undefined,
      payoff: (manual.payoff || '').trim() || undefined,
    });
    setManual(null);
    await load();
    stickRef.current = true;
  });
  const decide = (body) => act(async () => {
    // Picking is instant and model-free: the Task-Checker runs only from the
    // explicit button on its invite bubble (recheckTask below).
    await papi(`${base}/task-choose`, body);
    await load();
    if (onChanged) onChanged('meta');
  });
  // Re-run the Task-Checker over the current chosen card (the old verdict went
  // stale after an edit). The explicit ask forces past the content dedup.
  const recheckTask = () => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); stickRef.current = true; armPending('tfact');
    const r = await papi(`${base}/task-recheck`, {});
    noteCheck(r && r.check);
    await load();
  });
  // Manual per-card checklist run (attaches the gate + its per-criterion notes).
  const gateCard = (num) => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); stickRef.current = true; armPending('tcheck');
    await papi(`${base}/task-gate-card`, { num });
    await load();
  });
  // Redo one card from its gate's breakdown notes → appends a new version (streams like gen).
  const redoCard = (num) => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); stickRef.current = true; armPending('tgen');
    await papi(`${base}/task-redo-card`, { num });
    await load();
  });
  // Switch the active version of a card (‹ i/N › switcher). No model call.
  const switchVersion = (num, active) => act(async () => {
    await papi(`${base}/task-card-version`, { num, active });
    await load();
  });
  // In-place edit of a card's active version (any card — manual or generated).
  const editCard = (num, fields) => act(async () => {
    await papi(`${base}/task-edit-card`, { num, ...fields });
    await load();
    if (onChanged) onChanged('meta');
  });
  // Delete a card (any card). Clears the choice server-side if it was chosen.
  const deleteCard = (num) => act(async () => {
    await papi(`${base}/task-delete-card`, { num });
    await load();
    if (onChanged) onChanged('meta');
  });
  const startWrite = () => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); armPending('writer');
    await papi(`${base}/critic-loop`, {});
    await load();
    stickRef.current = true;
  });
  // First Author draft ONLY — the loop pauses before the review (step mode).
  const startWriteStep = () => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); armPending('writer');
    await papi(`${base}/critic-loop`, { step: 'writer' });
    await load();
    stickRef.current = true;
  });
  const sendWriterNote = () => act(async () => {
    const text = comment.trim();
    if (!text) throw new Error('Write a note — it goes to the Author as a rewrite instruction');
    startedRef.current = true; liveRef.current = blankTurn(); armPending('writer');
    await papi(`${base}/writer-note`, { note: text, step: noteStep ? 'writer' : undefined });
    setComment('');
    await load();
    stickRef.current = true;
  });
  // One review pass (Critic + Fact-checker) over the tip story, then pause.
  const reviewOnce = () => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); liveFactRef.current = blankTurn(); stickRef.current = true; armPending('critic');
    await papi(`${base}/continue-branch`, { step: 'review' });
    // Optimistic 'running' — same reasoning as continueBranch below.
    setWriteLoop((prev) => (prev ? { ...prev, status: 'running' } : prev));
  });
  // One Author rewrite from the pending critique, then pause before the review.
  const authorOnce = () => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); stickRef.current = true; armPending('writer');
    await papi(`${base}/continue-branch`, { step: 'writer' });
    setWriteLoop((prev) => (prev ? { ...prev, status: 'running' } : prev));
  });
  // Save a hand-written Author turn (no model call): a sibling variant of an
  // existing turn, or the Author's next message after the tip review.
  const saveManualTurn = (text) => act(async () => {
    const payload = manualTurn.mode === 'replace'
      ? { siblingOf: manualTurn.nodeId, body: text }
      : { atTip: true, body: text };
    await papi(`${base}/manual-turn`, payload);
    setManualTurn(null);
    await load();
    stickRef.current = true;
  });
  const continueBranch = () => act(async () => {
    // Continue from the tip story: the first turn is the review (critic + facts).
    startedRef.current = true; liveRef.current = blankTurn(); liveFactRef.current = blankTurn(); stickRef.current = true; armPending('critic');
    await papi(`${base}/continue-branch`, {});
    // Flip the loop to 'running' optimistically. The backend marks it running at
    // start but only emits a pipeline_loop event (which carries the status) when a
    // message LANDS — so between the click and the first turn finishing, the local
    // status would stay 'interrupted' and the branch tip would reappear. The real
    // status arrives via SSE and takes over from here.
    setWriteLoop((prev) => (prev ? { ...prev, status: 'running' } : prev));
  });
  const approveVersion = (messageId) => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn(); armPending('formal');
    await papi(`${base}/approve-version`, messageId ? { messageId } : {});
    await load();
    stickRef.current = true;
    if (onChanged) onChanged('meta');
  });
  // Regenerate one turn with an edited prompt -> a new sibling branch. With
  // thenContinue the backend rolls the new Author tip straight into the
  // review→rewrite loop and runs it to the end (approval / round cap + formalizer).
  const regenerate = (nodeId, role, system, user, model, thenContinue = false) => act(async () => {
    startedRef.current = true; liveRef.current = blankTurn();
    if (thenContinue) liveFactRef.current = blankTurn();
    armPending(role || 'writer');
    await papi(`${base}/regenerate`, { messageId: nodeId, system, user, model, thenContinue });
    setRegen(null);
    await load();
    stickRef.current = true;
    if (thenContinue) setWriteLoop((prev) => (prev ? { ...prev, status: 'running' } : prev));
  });
  // Switch the active branch (server re-materializes the active path).
  const switchBranch = (nodeId) => act(async () => {
    await papi(`${base}/switch-branch`, { nodeId });
    await load();
  });
  // Bring a parked past run back as the live dialogue. Lossless swap: the
  // current dialogue takes the restored run's slot in the history, so this is
  // freely reversible — no confirmation needed.
  const restorePastRun = (branchId, runEntryId) => act(async () => {
    await papi(`${base}/restore-write-run`, { branchId, runEntryId });
    await load();
  });
  // Dialogues displaced by a restart of the SAME card — parked in the active
  // decision branch's history. Shown as a fold at the top of the write feed.
  const activeWriteBranch = writeBranches.find((b) => b.active && b.id) || null;
  const pastRuns = (activeWriteBranch && activeWriteBranch.runs) || [];
  const renderPastRuns = () => (pastRuns.length ? (
    <details className="pd-fold pd-pastruns">
      <summary title="Dialogues replaced by a restart of this card — Restore swaps one back; the current dialogue takes its place here, nothing is lost">
        ⏪ Past runs of this card ({pastRuns.length})
      </summary>
      {pastRuns.map((r) => (
        <div key={r.id} className="pd-pastrun-row">
          <span className="pd-muted">
            {r.parked_at ? fmtTime(r.parked_at) : '—'} · {r.rounds_done || 0} round(s) · {r.status || '?'}
            {r.writer_model ? ` · ${shortModel(r.writer_model)}` : ''}
          </span>
          <span className="pd-spacer" />
          <button className="pd-btn pd-btn--sm" disabled={busy || anyRunning}
            title="Make this dialogue the live one again (lossless swap)"
            onClick={() => restorePastRun(activeWriteBranch.id, r.id)}>
            Restore
          </button>
        </div>
      ))}
    </details>
  ) : null);
  // (Decision branches are switched by Choose/Re-pick on a card — picking IS
  // branching; the badge on a card marks an existing tail.)
  // Stop targets THIS point's slot only — parallel agents on other points keep going.
  const stop = () => act(async () => { if (runId) await papi(`/runs/${runId}/stop`, { uuid }); });
  // Refresh the parent (map/list) only on close, if a generation changed anything —
  // never mid-work, so the page doesn't reload while you're in the dialog. Signal
  // 'point' + uuid so the parent patches just this location's content in place,
  // instead of the whole-quest refetch that blanks and flashes the list.
  const handleClose = () => {
    if (dirtyRef.current && onChanged) { dirtyRef.current = false; onChanged('point', uuid); }
    onClose();
  };

  // Writing can start with a picked card, an explicit no-task pick, or NO
  // decision at all — a point without a decision is a no-task point by default
  // (the Author writes a pure story), so nobody has to click "No task" per point.
  const canStartWrite = !choice || choice.mode === 'task' || choice.mode === 'no_task';
  // Chosen-card test: stable id first, legacy num fallback (pre-migration data).
  const isChosen = (c) => !!(choice && choice.mode === 'task'
    && (choice.cardId ? choice.cardId === c.id : choice.num === c.num));
  // Candidates that already own a decision branch (badge on the card).
  const branchOfCard = (c) => writeBranches.find((b) => b.mode === 'task'
    && (b.cardId ? b.cardId === c.id : (b.num != null && b.num === c.num))) || null;

  // Live/optimistic bubble. Shows from the CLICK (pending placeholder) and is
  // taken over by the SSE stream on its 'turn'. Sections: 'task' roles render
  // above the Decision fork, 'branch' (the Task-Checker — its verdict belongs
  // to the decision) right below the fork with the branch's checks, write
  // roles at the feed bottom — a finished bubble lands where it streamed.
  const LIVE_SECTION = { tgen: 'task', tcheck: 'task', tfact: 'branch' };
  // One live bubble. `lv` is a turn (role + streamed segs); `pendingRole` shows an
  // optimistic "starting…" bubble from a click before the first SSE 'turn' lands.
  const renderLiveBubble = (lv, pendingRole, key) => {
    const streaming = !!lv.role;
    const role = streaming ? lv.role : (pendingRole || null);
    if (!role || role === 'tres') return null; // Researcher streams in its pinned bubble
    const conf = ROLE[role] || ROLE.writer;
    const hasContent = streaming && lv.segs.length > 0;
    const liveCards = streaming && role === 'tgen'
      ? parseCandidatesText(lv.segs.filter((s) => s.t === 'text').map((s) => s.text).join(''))
          .filter((c) => c.question || c.answer || c.type || c.payoff)
      : null;
    // Fact-checker: parse the streamed answer into fact rows live (like tgen's
    // cards). The raw ledger text is hidden; reasoning / web-search bubbles stay.
    const liveFacts = streaming && role === 'factcheck'
      ? parseFactLedgerText(lv.segs.filter((s) => s.t === 'text').map((s) => s.text).join(''))
      : null;
    const hasReasoning = streaming && lv.segs.some((s) => s.t !== 'text');
    // Critic: split the streamed answer into human prose (shown live) and the
    // machine json block, parsed leniently so summary + findings appear as they
    // stream in (the block comes last, after the prose passes).
    const criticText = streaming && role === 'critic'
      ? lv.segs.filter((s) => s.t === 'text').map((s) => s.text).join('')
      : '';
    const liveCritic = role === 'critic' ? parseCriticLiveText(criticText) : null;
    const liveCriticProse = role === 'critic' ? criticProse(criticText) : '';
    const hasCriticFindings = !!(liveCritic && (liveCritic.summary || liveCritic.blockers.length || liveCritic.optional.length || liveCritic.mechanical.length));
    // Placeholder shown in the fact-checker's bubble until its first token streams.
    const placeholder = role === 'factcheck' && !hasContent ? 'Fact-check in progress…' : null;
    return (
      <div key={key} className={`pd-turn pd-turn--${conf.side} pd-turn--${role} pd-turn--live`}>
        <div className="pd-turn-head">
          <span className="pd-rolechip pd-rolechip--static">{conf.label}</span>
          {streaming && lv.round != null && <span className="pd-meta">round {lv.round}</span>}
          <span className="pd-meta pd-meta--live">{lv.ended ? 'finishing…' : hasContent ? 'streaming…' : 'starting…'}</span>
        </div>
        <div className="pd-turn-body">
          {streaming && lv.status && (
            <div className="pd-livestatus"><span className="pd-livedot" />{lv.status}</div>
          )}
          {role === 'factcheck' ? (
            <>
              {hasContent && <SegmentsView segments={lv.segs} streaming hideText />}
              <LiveFactLedger entries={liveFacts} />
              {liveFacts.length === 0 && !hasReasoning && placeholder && <div className="pd-muted">{placeholder}</div>}
            </>
          ) : role === 'critic' ? (
            <>
              {hasReasoning && <SegmentsView segments={lv.segs} streaming hideText />}
              {liveCriticProse ? <div className="pd-crit-prose"><Md>{liveCriticProse}</Md></div> : null}
              {liveCritic && <CriticFindings summary={liveCritic.summary} blockers={liveCritic.blockers} optional={liveCritic.optional} mechanical={liveCritic.mechanical} />}
              {!liveCriticProse && !hasCriticFindings && !hasReasoning && <div className="pd-muted">Reviewing the story…</div>}
            </>
          ) : liveCards && liveCards.length > 0 ? (
            <div className="pd-cards">
              {liveCards.map((c, ci) => <CandidateCard key={`${c.num}-${ci}`} cand={c} live />)}
            </div>
          ) : hasContent ? (
            <SegmentsView segments={lv.segs} streaming />
          ) : placeholder ? (
            <div className="pd-muted">{placeholder}</div>
          ) : null}
        </div>
      </div>
    );
  };
  // The critic and fact-checker stream at the same time — render both bubbles,
  // critic above fact-checker (fixed order, independent of who returns first).
  // NOT gated on anyRunning — that comes from a network load() and lags the SSE
  // stream; 'done'/reload reset the bubbles, so nothing lingers.
  // A live bubble whose stored message is already in the feed is a leftover
  // (a replayed trace after a reconnect, or a lost loop event): a message of the
  // same role created after the turn started means that turn is over. Turns of
  // one role never overlap, so the check is safe for both bubbles.
  const liveFinished = (lv) => !!(lv.role && lv.startedAt && messages.some((m) => (
    m.role === lv.role && (Date.parse(m.created_at || '') || 0) >= lv.startedAt
  )));
  const renderLive = (section) => {
    const out = [];
    const lv = liveRef.current;
    const mainRole = lv.role || (pending ? pending.role : null);
    if (mainRole && mainRole !== 'tres' && (LIVE_SECTION[mainRole] || 'write') === section && !liveFinished(lv)) {
      out.push(renderLiveBubble(lv, pending && pending.role, 'live-main'));
    }
    if (section === 'write' && liveFactRef.current.role === 'factcheck' && !liveFinished(liveFactRef.current)) {
      out.push(renderLiveBubble(liveFactRef.current, null, 'live-fact'));
    }
    const items = out.filter(Boolean);
    return items.length ? items : null;
  };
  const taskStatus = taskLoop ? taskLoop.status : (choice && choice.mode === 'no_task' ? 'no_task' : null);
  const writeStatus = writeLoop ? writeLoop.status : null;
  // Task phase now: the human hand-writes cards (helped by the Researcher's
  // facts); no generator composer. The composer only appears in the writing
  // phase (writer note) or as the brief note before Write.
  const lastWriter = [...messages].reverse().find((m) => m.role === 'writer');
  const lastWriterId = lastWriter ? lastWriter.id : null;
  const approvedMsgId = (writeLoop && writeLoop.approved_message_id) || null;
  const prevWriterBodyById = {};
  {
    let lastBody = null;
    messages.forEach((m) => {
      if (m.role !== 'writer') return;
      if (lastBody != null) prevWriterBodyById[m.id] = lastBody;
      lastBody = m.body != null ? m.body : '';
    });
  }
  const writerCount = messages.filter((m) => m.role === 'writer').length;
  // Active-branch tip that was regenerated (custom) and has no continuation yet.
  const writeMsgs = messages.filter((m) => m.phase === 'write' && m.role !== 'brief');
  const lastWrite = writeMsgs[writeMsgs.length - 1];
  // Offer to continue the review→rewrite loop forward. Cases:
  //   • a regenerated (custom) Author tip with no continuation yet;
  //   • ANY interrupted loop that has an Author story — the tip may be a story
  //     (backend reviews it) or a critic/fact turn (backend re-sends the critique
  //     to the Author to rewrite). Either way continue-branch handles it.
  const writeStatus0 = writeLoop ? writeLoop.status : null;
  const hasStory = writeMsgs.some((m) => m.role === 'writer' && m.body);
  // Custom tip = an Author story that exists outside the finished loop flow: a
  // regeneration with an edited prompt, or a hand-written (manual) turn.
  const tipCustom = !!(lastWrite && lastWrite.role === 'writer'
    && (!lastWrite.branch_meta || lastWrite.branch_meta.is_tip)
    && ((lastWrite.prompt && lastWrite.prompt.custom) || lastWrite.manual));
  const tipIsStory = !!(lastWrite && lastWrite.role === 'writer' && lastWrite.body);
  // The last review round's verdicts (everything after the last story): a
  // pending critique means an Author rewrite is the natural next step — even
  // when the loop settled at the round cap / stopped / errored. Mirrors the
  // server's reconstructLastReviewFeedback test.
  let lastWriterIdx = -1;
  writeMsgs.forEach((m, i) => { if (m.role === 'writer' && m.body) lastWriterIdx = i; });
  const afterWriter = lastWriterIdx >= 0 ? writeMsgs.slice(lastWriterIdx + 1) : [];
  const tipCritic = [...afterWriter].reverse().find((m) => m.role === 'critic');
  const tipFact = [...afterWriter].reverse().find((m) => m.role === 'factcheck');
  const pendingCritique = !!(tipCritic && !(tipCritic.approved && (!tipFact || tipFact.approved)));
  // Story tip → offer a review (once / to the end). Critique tip → offer an
  // Author rewrite (once / by hand / to the end). Hidden on approved loops.
  // Also hide the moment a continue is armed (`pending`): continue-branch streams
  // via trace events that don't flip the loop status to 'running' right away, so
  // without this the tip would linger on screen while the run has visibly started.
  const showStoryTip = tipIsStory && (writeStatus0 === 'interrupted' || writeStatus0 === 'paused' || tipCustom);
  const showCritiqueTip = !tipIsStory && pendingCritique && writeStatus0 !== 'approved';
  const showBranchTip = !anyRunning && !pending && hasStory && (showStoryTip || showCritiqueTip);

  // ------- render one writing-phase agent turn (chat bubble) -------
  const renderWritingTurn = (m) => {
    const conf = ROLE[m.role] || ROLE.writer;
    const side = conf.side;
    const accepted = m.role === 'writer' && approvedMsgId && m.id === approvedMsgId;
    const canRegen = ['writer', 'critic', 'factcheck', 'formal'].includes(m.role);
    const nodeId = (m.branch_meta && m.branch_meta.node_id) || m.id;
    const hasSegs = Array.isArray(m.segments) && m.segments.length > 0;
    const draftOpen = m.role === 'formal' || m.id === lastWriterId;
    return (
      <div key={m.id} className={`pd-turn pd-turn--${side} pd-turn--${m.role}${accepted ? ' pd-turn--accepted' : ''}`}>
        <div className="pd-turn-head">
          <button type="button" className="pd-rolechip" onClick={() => canRegen && setRegen({ node: { id: nodeId, role: m.role, round: m.round }, mode: 'view' })} title="View prompt">
            {conf.label}
          </button>
          {m.round != null && <span className="pd-meta">round {m.round}</span>}
          {m.created_at && <span className="pd-meta">{fmtTime(m.created_at)}</span>}
          {/* Which model produced THIS turn — recorded when it ran, so an old
              turn keeps showing the model it was actually written by. */}
          {m.model && <span className="pd-meta pd-meta--model" title={m.model}>{shortModel(m.model)}</span>}
          <BranchSwitcher meta={m.branch_meta} onSwitch={switchBranch} disabled={busy || anyRunning} />
          {m.prompt && m.prompt.custom && <span className="pd-pbadge">{Icon.pencil()}custom prompt</span>}
          {m.manual && <span className="pd-pbadge" title={`Hand-written turn${m.author ? ` · ${m.author}` : ''}`}>{Icon.pencil()}your text</span>}
          {accepted && <span className="pd-accepted">{Icon.check()}Accepted{writeLoop && writeLoop.approved_kind === 'human' ? ' · human' : ''}</span>}
          {m.role === 'critic' && (m.approved
            ? <Verdict kind="ok">Approved</Verdict>
            : <Verdict kind="warn">Needs changes{m.marker_found === false ? ' (no marker)' : ''}</Verdict>)}
          {m.role === 'factcheck' && (m.approved
            ? <Verdict kind="ok">Facts clean</Verdict>
            : <Verdict kind="warn">Fact problems{m.marker_found === false ? ' (no marker)' : ''}</Verdict>)}
          <span className="pd-spacer" />
          {(m.role === 'critic' || m.role === 'factcheck') && (
            <button type="button" className={`pd-fbcopy${copiedFbId === nodeId ? ' pd-fbcopy--done' : ''}`}
              onClick={() => copyRoundFeedback(nodeId)}
              title="Copy this round's review exactly as it goes to the Author (assembled text, not these cards)"
              aria-label="Copy as sent to the Author">
              {copiedFbId === nodeId ? Icon.check() : Icon.copy()}
            </button>
          )}
          {m.role === 'writer' && !anyRunning && (
            <button type="button" className="pd-regenbtn" onClick={() => setManualTurn({ mode: 'replace', nodeId, body: m.body || '' })}
              title="Replace this story with your own text — it lands as a sibling variant, the original stays switchable">
              {Icon.pencil()}Edit text…
            </button>
          )}
          {canRegen && !anyRunning && (
            <button type="button" className="pd-regenbtn" onClick={() => setRegen({ node: { id: nodeId, role: m.role, round: m.round }, mode: 'edit' })}>
              {Icon.refresh()}Regenerate…
            </button>
          )}
          {m.role === 'writer' && !accepted && !anyRunning && (
            <button type="button" disabled={busy} onClick={() => approveVersion(m.id)} className="pd-btn pd-btn--ok pd-btn--sm"
              title="Accept this Author version — formalizes it into the point's fields">
              Approve
            </button>
          )}
        </div>
        {m.role === 'formal' && <div className="pd-formal-note">{Icon.check()}Formalized → quest fields</div>}
        {/* Full running fact list each round, with this round's diff highlighted
            in place (new tinted, dropped struck). Raw markup / edit commands are
            not shown — see FactLedger. */}
        {m.role === 'factcheck' && <FactLedger msg={m} />}
        {/* Critic's structured findings (summary + blockers/optional) shown as
            cards, like the fact-checker's ledger. The raw json block is not
            rendered — the prose passes live in a fold in the body below. */}
        {m.role === 'critic' && <CriticFindings summary={m.summary} blockers={m.blockers} optional={m.optional} mechanical={m.mechanical} />}
        <div className="pd-turn-body">
          {(() => {
            if (m.role === 'writer' && diffMode !== 'off' && prevWriterBodyById[m.id] != null) {
              return (<>
                {hasSegs && <SegmentsView segments={m.segments} hideText />}
                <DiffView oldText={prevWriterBodyById[m.id]} newText={m.body || ''} mode={diffMode} />
              </>);
            }
            if (m.role === 'factcheck') {
              // The raw answer is the ledger markup (already rendered above by
              // FactLedger) plus the remarks. Keep reasoning / web-search bubbles
              // (hideText drops the raw text), then show only the clean remarks.
              return (<>
                {hasSegs && <SegmentsView segments={m.segments} hideText />}
                {m.body ? <Md>{m.body}</Md> : null}
              </>);
            }
            if (m.role === 'critic') {
              // Findings already shown above as cards. Keep reasoning bubbles
              // (hideText drops the raw text incl. the json block); the human
              // prose passes go in a fold so the cards stay front and center.
              // Old (pre-v2) critic turns have no findings — show the body plain.
              const structured = !!(m.summary || (m.blockers && m.blockers.length) || (m.optional && m.optional.length));
              return (<>
                {hasSegs && <SegmentsView segments={m.segments} hideText />}
                {m.body ? (structured
                  ? <details className="pd-fold"><summary>critic's full analysis</summary><div className="pd-crit-prose"><Md>{m.body}</Md></div></details>
                  : <Md>{m.body}</Md>) : null}
              </>);
            }
            if (hasSegs) {
              return (<>
                <SegmentsView segments={m.segments} hideText={!!m.draft} />
                {m.draft && <DraftView draft={m.draft} defaultOpen={draftOpen} />}
              </>);
            }
            return (<>
              {m.reasoning && <ReasoningView thinking={m.reasoning.thinking} tools={m.reasoning.tools} />}
              {m.draft ? <DraftView draft={m.draft} defaultOpen={draftOpen} /> : <Md>{m.body}</Md>}
            </>);
          })()}
        </div>
      </div>
    );
  };

  // Persistent Researcher bubble — always in the feed. Empty → a Focus field and
  // a "Gather facts" button; after a pass → the facts stream into this same
  // bubble. Every pass is a VARIANT (◀ n / N ▶) tagged with the focus it ran
  // with; the Focus field shows the variant in view, and gathering again with
  // another topic adds a variant next to it instead of replacing anything.
  const renderResearcherBubble = () => {
    const variants = researchVariants(taskLoop);
    const resMsg = activeResearch(taskLoop);
    const variantMeta = resMsg && variants.length > 1 ? {
      count: variants.length,
      index: Math.max(0, variants.findIndex((m) => m.id === resMsg.id)),
      sibling_ids: variants.map((m) => m.id),
    } : null;
    const fname = `facts-${(pointName || 'point').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'point'}.md`;
    // When a Researcher pass is running, the stream lands INSIDE this bubble (not
    // a separate live bubble) — Re-gather updates the one Researcher message.
    const lv = liveRef.current;
    // Not gated on anyRunning (a lagging network load()) — see renderLive.
    const liveHere = lv.role === 'tres' || (!lv.role && pending && pending.role === 'tres');
    const liveContent = lv.role === 'tres' && lv.segs.length > 0;
    return (
      <div className={`pd-turn pd-turn--left pd-turn--tres${liveHere ? ' pd-turn--live' : ''}`}>
        <div className="pd-turn-head">
          <button type="button" className="pd-rolechip" onClick={() => setPromptRole('tres')}>Researcher</button>
          {liveHere
            ? <span className="pd-meta pd-meta--live">{liveContent ? 'streaming…' : 'starting…'}</span>
            : (resMsg && resMsg.created_at && <span className="pd-meta">{fmtTime(resMsg.created_at)}</span>)}
          {!liveHere && resMsg && (
            <span className={`pd-focustag${resMsg.focus ? '' : ' pd-focustag--none'}`} title="The topic this facts set was gathered with">
              {resMsg.focus ? `focus: ${resMsg.focus}` : 'no focus'}
            </span>
          )}
          {!liveHere && variantMeta && (
            <BranchSwitcher meta={variantMeta} onSwitch={selectResearch} disabled={busy || anyRunning} />
          )}
          <span className="pd-spacer" />
          <button type="button" className={`pd-dlbtn pd-copybtn${guideCopied ? ' pd-copybtn--done' : ''}`} onClick={copyGuidePrompt}
            title="Copy a ready-to-paste brief (authoring guide + this location & city) for a chat assistant">
            {guideCopied ? Icon.check() : Icon.copy()}
            {/* Both labels stacked in one grid cell → width = widest, so the swap
                never resizes the button. */}
            <span className="pd-copyswap">
              <span className="pd-copyswap-idle">Copy guide prompt</span>
              <span className="pd-copyswap-done">Copied!</span>
            </span>
          </button>
          {resMsg && (
            <button type="button" className={`pd-dlbtn pd-copybtn${fullCopied ? ' pd-copybtn--done' : ''}`} onClick={copyFullPrompt}
              title="Guide prompt with the Researcher's facts spliced in — one paste for a chat assistant">
              {fullCopied ? Icon.check() : Icon.copy()}
              <span className="pd-copyswap">
                <span className="pd-copyswap-idle">Copy full prompt</span>
                <span className="pd-copyswap-done">Copied!</span>
              </span>
            </button>
          )}
          {!liveHere && resMsg && (
            <button type="button" className="pd-dlbtn" onClick={() => downloadText(fname, resMsg.body)}
              title="Download the collected facts as a .md file">
              {Icon.download()}Download
            </button>
          )}
          <button disabled={busy || anyRunning} onClick={research} className="pd-btn pd-btn--gen pd-btn--sm"
            title={resMsg ? 'Runs the Researcher again — the result is added as a new variant, this one stays' : 'One web-search pass over this place'}>
            {resMsg ? 'Gather again' : 'Gather facts'}
          </button>
        </div>
        {/* Focus (optional): the topic most of the facts should centre on. Sent
            with the next Gather; empty = the plain prompt, as before. */}
        <div className="pd-focusrow">
          <input type="text" className="pd-focus" value={focusDraft} disabled={busy || anyRunning}
            placeholder="Focus (optional) — a topic to centre the facts on, e.g. Музыка"
            title="Optional. Most of the facts and sources will follow this topic; leave empty for the general sweep"
            onChange={(e) => setFocusDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy && !anyRunning) { e.preventDefault(); research(); } }} />
        </div>
        <div className="pd-turn-body">
          {liveHere ? (
            <>
              {lv.status && <div className="pd-livestatus"><span className="pd-livedot" />{lv.status}</div>}
              {liveContent ? <SegmentsView segments={lv.segs} streaming /> : null}
            </>
          ) : resMsg ? (
            <>
              {Array.isArray(resMsg.segments) && resMsg.segments.some((s) => s.t !== 'text') && (
                <details className="pd-reason"><summary>reasoning</summary>
                  <div className="pd-reason-body">
                    {resMsg.segments.filter((s) => s.t !== 'text').map((s, j) => (s.t === 'tool'
                      ? <div key={j} className="pd-tool">{s.text}</div> : <span key={j}>{s.text}</span>))}
                  </div>
                </details>
              )}
              <Md>{resMsg.body}</Md>
            </>
          ) : (
            <div className="pd-muted">Collects facts + sources about this place — a reference you read while writing cards. Not shown to the writer. Type a focus above to centre most of the facts on one topic.</div>
          )}
        </div>
      </div>
    );
  };

  // Persistent "Your cards" bubble — always in the feed, alongside the Researcher
  // (fill cards in parallel with gathering facts). Holds the add-card form, your
  // hand-written cards, and the "No task" stub card. One message for the stage.
  const renderCardsBubble = () => {
    const manualCards = messages
      .filter((m) => m.role === 'manual')
      .flatMap((m) => (Array.isArray(m.candidates) ? m.candidates : []));
    const noTaskChosen = !!(choice && choice.mode === 'no_task');
    const noTaskDefault = !choice; // no decision = no-task by default
    return (
      <div className="pd-turn pd-turn--right pd-turn--human pd-turn--full">
        <div className="pd-turn-head">
          <span className="pd-rolechip pd-rolechip--static">Your cards</span>
          {manualCards.length > 0 && <span className="pd-cardstage-count">{manualCards.length} added</span>}
          <span className="pd-spacer" />
          {!manual && (
            <button disabled={busy} onClick={() => setManual({ question: '', answer: '', type: '', payoff: '' })} className="pd-addcard">
              {Icon.plus()}{manualCards.length > 0 ? 'Write another card' : 'Write a card'}
            </button>
          )}
        </div>
        <div className="pd-turn-body">
          <div className="pd-cards">
            {manualCards.map((c, ci) => (
              <CandidateCard key={`${c.num}-${ci}`} cand={c} chosen={isChosen(c)}
                branch={branchOfCard(c)}
                busy={busy || anyRunning} onChoose={(c) => decide({ cardId: c.id || undefined, num: c.num, notes: notesDraft.trim() || undefined })}
                onEdit={editCard} onDelete={deleteCard} />
            ))}
            {manual && (
              <div className="pd-card">
                <div className="pd-card-head">
                  <span className="pd-card-num">✍ new card</span>
                </div>
                <div className="pd-cardedit">
                  <textarea className="pd-textarea" style={{ minHeight: 46 }} autoFocus placeholder="The riddle, worded the way you'll ask it"
                    value={manual.question} onChange={(e) => setManual((s) => ({ ...s, question: e.target.value }))} />
                  <textarea className="pd-textarea" style={{ minHeight: 32 }} placeholder="Answer — 1–2 words; equivalent spellings split by ;"
                    value={manual.answer} onChange={(e) => setManual((s) => ({ ...s, answer: e.target.value }))} />
                  <div className="pd-typepills">
                    {['НА МЕСТЕ', 'ЭРУДИЦИЯ', 'ЛОГИКА'].map((t) => (
                      <button key={t} type="button" className={`pd-pill${manual.type === t ? ' pd-pill--on' : ''}`}
                        onClick={() => setManual((s) => ({ ...s, type: s.type === t ? '' : t }))}>{t}</button>
                    ))}
                  </div>
                  <textarea className="pd-textarea" style={{ minHeight: 32 }} placeholder="Payoff — optional, shown after the answer"
                    value={manual.payoff} onChange={(e) => setManual((s) => ({ ...s, payoff: e.target.value }))} />
                  <div className="pd-card-actions">
                    <button className="pd-btn pd-btn--ok pd-btn--sm" disabled={busy || !manual.question.trim() || !manual.answer.trim()} onClick={addManualCard}>
                      {Icon.plus()}Add card
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className={`pd-card pd-card--notask${noTaskChosen || noTaskDefault ? ' pd-card--chosen' : ''}`}>
              <div className="pd-card-head">
                <span className="pd-card-num">No task</span>
                {noTaskChosen && <span className="pd-card-chosen">chosen</span>}
                {noTaskDefault && <span className="pd-card-chosen" title="No card picked — the point is written as a pure story unless you choose a card">default</span>}
              </div>
              <div className="pd-card-q pd-muted">Leave this point with no riddle — the writer makes a pure story. This is the default until a card is chosen.</div>
              {!noTaskChosen && !noTaskDefault && (
                <div className="pd-card-actions">
                  <button disabled={busy || anyRunning} onClick={() => decide({ no_task: true })} className="pd-btn pd-btn--sm pd-btn--ghost">Choose</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const taskAgentTabs = taskLoop && (
    <>
      {taskLoop.research_prompt && (
        <AgentTab role="tres" model={modelOf(writeLoop, taskLoop, 'tres')} version={promptVer(taskLoop.research_prompt)} onClick={() => setPromptRole('tres')} />)}
      {/* Generator + Checklist are retired to Legacy — tabs show only for older
          points whose exchange still carries their snapshots. */}
      {taskLoop.gen_prompt && <>{taskLoop.research_prompt && <span className="pd-sep">·</span>}
        <AgentTab role="tgen" model={modelOf(writeLoop, taskLoop, 'tgen')} version={promptVer(taskLoop.gen_prompt)} onClick={() => setPromptRole('tgen')} /></>}
      {taskLoop.check_prompt && <><span className="pd-sep">·</span>
        <AgentTab role="tcheck" model={modelOf(writeLoop, taskLoop, 'tcheck')} version={promptVer(taskLoop.check_prompt)} onClick={() => setPromptRole('tcheck')} /></>}
      {taskLoop.fact_prompt && <><span className="pd-sep">·</span>
        <AgentTab role="tfact" model={modelOf(writeLoop, taskLoop, 'tfact')} version={promptVer(taskLoop.fact_prompt)} onClick={() => setPromptRole('tfact')} /></>}
    </>
  );
  const writeAgentTabs = writeLoop && (
    <>
      <AgentTab role="writer" model={modelOf(writeLoop, taskLoop, 'writer')} version={promptVer(writeLoop.writer_prompt)} onClick={() => setPromptRole('writer')} />
      <span className="pd-sep">·</span>
      <AgentTab role="critic" model={modelOf(writeLoop, taskLoop, 'critic')} version={promptVer(writeLoop.critic_prompt)} onClick={() => setPromptRole('critic')} />
      {writeLoop.fact_prompt && <><span className="pd-sep">·</span>
        <AgentTab role="factcheck" model={modelOf(writeLoop, taskLoop, 'factcheck')} version={promptVer(writeLoop.fact_prompt)} onClick={() => setPromptRole('factcheck')} /></>}
      {writeLoop.formal_prompt && <><span className="pd-sep">·</span>
        <AgentTab role="formal" model={modelOf(writeLoop, taskLoop, 'formal')} version={promptVer(writeLoop.formal_prompt)} onClick={() => setPromptRole('formal')} /></>}
    </>
  );

  return createPortal(
    <div className="pd-overlay">
      <style>{PD_CSS}</style>
      <div className="pd-modal" role="dialog" aria-modal="true">
        <div className="pd-head">
          <span className="pd-title">Point dialog{pointName ? <span className="pd-loc"> — {pointName}</span> : ''}</span>
          <span className="pd-spacer" />
          {/* Switch any agent's model before firing a turn from this dialog. */}
          <button className="pd-agenttab" onClick={() => setShowModels(true)} title="Pick the model each agent runs on">⌁ Models</button>
          <button className="pd-iconbtn" onClick={handleClose} aria-label="Close">{Icon.close()}</button>
        </div>
        {showModels && <ModelsLegend onClose={() => setShowModels(false)} />}

        {loading && <div className="pd-status"><span className="pd-muted">Loading…</span></div>}
        {error && <div className="pd-error pd-error--bar">{error}</div>}

        {!loading && (
          <>
            <div className="pd-status">
              {taskStatus && <StatusChip status={taskStatus} map={TASK_STATUS} />}
              {writeStatus && <StatusChip status={writeStatus} map={WRITE_STATUS} />}
              {writeLoop && <span className="pd-meta">round {writeLoop.rounds_done || 0}/{writeLoop.max_rounds}</span>}
              <span className="pd-agents">
                {taskAgentTabs}
                {taskLoop && writeLoop && <span className="pd-sep">·</span>}
                {writeAgentTabs}
              </span>
            </div>

            <div className="pd-feed" ref={listRef} onScroll={(e) => { stickRef.current = isNearBottom(e.currentTarget); }}>
              {/* Two always-present stage bubbles: gather facts and write cards, in
                  parallel. Their content (facts / manual cards) lives inside them,
                  so those roles are skipped in the chronological loop below. */}
              {renderResearcherBubble()}
              {renderCardsBubble()}
              {messages.map((m) => {
                if (m.role === 'tres' || m.role === 'manual') return null;
                if (m.role === 'tbrief') {
                  return (
                    <details key={m.id} className="pd-fold pd-tbrief">
                      <summary>{m.auto ? `Auto-retry brief · ${fmtTime(m.created_at)}` : `Task brief (generator's opening message) · ${fmtTime(m.created_at)}`}</summary>
                      <div className="pd-pre pd-tbrief-body">{m.body}</div>
                    </details>
                  );
                }
                if (m.role === 'human') {
                  return (
                    <div key={m.id} className="pd-turn pd-turn--right pd-turn--human">
                      <div className="pd-turn-head">
                        <span className="pd-rolechip pd-rolechip--static">{m.author || 'editor'}</span>
                        {m.created_at && <span className="pd-meta">{fmtTime(m.created_at)}</span>}
                      </div>
                      <div className="pd-turn-body pd-pre">{m.body}</div>
                      {m.sent && m.sent !== m.body && (
                        <details className="pd-fold" style={{ padding: '0 14px 12px' }}>
                          <summary>as sent to the model</summary>
                          <div className="pd-pre pd-muted">{m.sent}</div>
                        </details>
                      )}
                    </div>
                  );
                }
                if (m.role === 'tres') {
                  const fname = `facts-${(pointName || 'point').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'point'}.md`;
                  return (
                    <div key={m.id} className="pd-turn pd-turn--left pd-turn--tres">
                      <div className="pd-turn-head">
                        <button type="button" className="pd-rolechip" onClick={() => setPromptRole('tres')}>Researcher</button>
                        {m.created_at && <span className="pd-meta">{fmtTime(m.created_at)}</span>}
                        <span className="pd-spacer" />
                        <button type="button" className="pd-dlbtn" onClick={() => downloadText(fname, m.body)}
                          title="Download the collected facts as a .md file">
                          {Icon.download()}Download
                        </button>
                      </div>
                      <div className="pd-turn-body">
                        {Array.isArray(m.segments) && m.segments.some((s) => s.t !== 'text') && (
                          <details className="pd-reason"><summary>reasoning</summary>
                            <div className="pd-reason-body">
                              {m.segments.filter((s) => s.t !== 'text').map((s, j) => (s.t === 'tool'
                                ? <div key={j} className="pd-tool">{s.text}</div> : <span key={j}>{s.text}</span>))}
                            </div>
                          </details>
                        )}
                        <Md>{m.body}</Md>
                      </div>
                    </div>
                  );
                }
                if (m.role === 'tgen') {
                  const cands = Array.isArray(m.candidates) ? m.candidates : [];
                  const prose = (m.body || '').split(/^===\s*КАНДИДАТ/m)[0].trim();
                  return (
                    <div key={m.id} className="pd-turn pd-turn--left pd-turn--tgen">
                      <div className="pd-turn-head">
                        <button type="button" className="pd-rolechip" onClick={() => setPromptRole('tgen')}>Generator</button>
                        {m.created_at && <span className="pd-meta">{fmtTime(m.created_at)}</span>}
                        {m.auto && <span className="pd-tag pd-tag--ghost">auto-retry</span>}
                        {m.cards_ok === false && cands.length > 0 && <span className="pd-tag pd-tag--warn">partial parse</span>}
                      </div>
                      <div className="pd-turn-body">
                        {Array.isArray(m.segments) && m.segments.some((s) => s.t !== 'text') && (
                          <details className="pd-reason"><summary>reasoning</summary>
                            <div className="pd-reason-body">
                              {m.segments.filter((s) => s.t !== 'text').map((s, j) => (s.t === 'tool'
                                ? <div key={j} className="pd-tool">{s.text}</div> : <span key={j}>{s.text}</span>))}
                            </div>
                          </details>
                        )}
                        {cands.length > 0 ? (
                          <>
                            {prose && <div style={{ marginBottom: 6 }}><Md>{prose}</Md></div>}
                            <div className="pd-cards">
                              {cands.map((c, ci) => (
                                <CandidateCard key={`${c.num}-${ci}`} cand={c} chosen={isChosen(c)}
                                  branch={branchOfCard(c)}
                                  busy={busy || anyRunning} onChoose={(c) => decide({ cardId: c.id || undefined, num: c.num, notes: notesDraft.trim() || undefined })}
                                  onGate={(n) => gateCard(n)} onRedo={(n) => redoCard(n)} onSwitch={(n, a) => switchVersion(n, a)}
                                  onEdit={editCard} onDelete={deleteCard} />
                              ))}
                            </div>
                          </>
                        ) : <Md>{m.body}</Md>}
                      </div>
                    </div>
                  );
                }
                if (m.role === 'choice') {
                  const noTask = choice.mode === 'no_task';
                  return (
                    <React.Fragment key={m.id}>
                      {/* Task-phase live/pending bubble sits ABOVE the fork. */}
                      {renderLive('task')}
                      <div className={`pd-decision${noTask ? ' pd-decision--none' : ''}`}>
                        <div className="pd-decision-head">
                          {noTask ? 'Decision: this point goes with no task'
                            : `Decision: ${(choice.parsed && choice.parsed.question) ? `«${choice.parsed.question.slice(0, 60)}${choice.parsed.question.length > 60 ? '…' : ''}»` : `candidate №${choice.num}`}`}
                          <span className="pd-meta"> · {choice.decided_by}{choice.decided_at ? ` · ${fmtTime(choice.decided_at)}` : ''}</span>
                        </div>
                        {!noTask && choice.notes && (
                          <div className="pd-decision-note"><span className="pd-microlabel">note for the brief · </span>{choice.notes}</div>
                        )}
                        {!anyRunning && (
                          <button className="pd-btn pd-btn--sm pd-btn--ghost" disabled={busy} onClick={() => decide({ clear: true })}>
                            {noTask ? 'Clear' : 'Clear choice'}
                          </button>
                        )}
                      </div>
                      {/* THE BRANCH SECTION: this decision's checker verdicts live
                          here, below the fork — pick another card and they swap
                          out together with the write phase. */}
                      {!noTask && checks.map((c, ci) => {
                        const stale = ci === checks.length - 1 && c.card != null && c.card !== choice.card;
                        return (
                          <div key={c.id} className="pd-turn pd-turn--right pd-turn--tfact">
                            <div className="pd-turn-head">
                              <button type="button" className="pd-rolechip" onClick={() => setPromptRole('tfact')}>Task-Checker</button>
                              {c.created_at && <span className="pd-meta">{fmtTime(c.created_at)}</span>}
                              {stale && <span className="pd-tag pd-tag--warn">card edited since</span>}
                            </div>
                            <div className="pd-turn-body">
                              {c.card != null && (
                                <details className="pd-reason"><summary>checked card</summary>
                                  <div className="pd-pre pd-muted" style={{ marginTop: 4 }}>{c.card}</div>
                                </details>
                              )}
                              {Array.isArray(c.segments) && c.segments.some((s) => s.t !== 'text') && (
                                <details className="pd-reason"><summary>reasoning</summary>
                                  <div className="pd-reason-body">
                                    {c.segments.filter((s) => s.t !== 'text').map((s, j) => (s.t === 'tool'
                                      ? <div key={j} className="pd-tool">{s.text}</div> : <span key={j}>{s.text}</span>))}
                                  </div>
                                </details>
                              )}
                              <Md>{c.body}</Md>
                            </div>
                          </div>
                        );
                      })}
                      {/* Task-Checker invite: picking never auto-runs the check —
                          this bubble is THE launch point, and doubles as the
                          re-check prompt when the chosen text drifted. */}
                      {(() => {
                        if (noTask || anyRunning || pending || busy) return null;
                        const checkedNow = checks.some((c) => c.card != null && c.card === choice.card);
                        if (checkedNow) return null;
                        const lastCheck = checks.length ? checks[checks.length - 1] : null;
                        // A verdict without a snapshot can neither confirm nor deny
                        // it covered the current text.
                        const legacy = !!(lastCheck && lastCheck.card == null);
                        return (
                          <div className="pd-turn pd-turn--right pd-turn--tfact">
                            <div className="pd-turn-head">
                              <button type="button" className="pd-rolechip" onClick={() => setPromptRole('tfact')}>Task-Checker</button>
                            </div>
                            <div className="pd-turn-body">
                              <div className="pd-muted" style={{ marginBottom: 8 }}>
                                {lastCheck
                                  ? (legacy
                                    ? 'The last verdict predates card snapshots — no way to confirm it covered this exact text.'
                                    : 'The card changed since its last check — the verdict above may be off.')
                                  : 'The chosen card has not been fact-checked yet.'}
                              </div>
                              <button type="button" className="pd-btn pd-btn--primary" onClick={recheckTask}>
                                {lastCheck ? 'Re-check card' : 'Run fact-check'}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      {renderLive('branch')}
                    </React.Fragment>
                  );
                }
                if (m.role === 'gate') {
                  return (
                    <div key={m.id} className={`pd-gate${m.passed === 0 ? ' pd-gate--dead' : ''}`}>
                      <span className="pd-gate-title">Gate:</span>{' '}
                      <button type="button" className="pd-linkish" onClick={() => setPromptRole('tcheck')}>Checklist</button>
                      <span className="pd-meta"> · {fmtTime(m.created_at)}</span>
                      <div className="pd-pre pd-muted" style={{ marginTop: 2 }}>{m.body}</div>
                    </div>
                  );
                }
                if (m.role === 'tfact') {
                  // Pre-migration leftover only: verdicts now live in the decision
                  // branch (rendered below the fork). Keep the shape so an
                  // un-migrated point still shows its history.
                  return (
                    <div key={m.id} className="pd-turn pd-turn--right pd-turn--tfact">
                      <div className="pd-turn-head">
                        <button type="button" className="pd-rolechip" onClick={() => setPromptRole('tfact')}>Task-Checker</button>
                        {m.created_at && <span className="pd-meta">{fmtTime(m.created_at)}</span>}
                      </div>
                      <div className="pd-turn-body">
                        {m.card != null && (
                          <details className="pd-reason"><summary>checked card</summary>
                            <div className="pd-pre pd-muted" style={{ marginTop: 4 }}>{m.card}</div>
                          </details>
                        )}
                        {Array.isArray(m.segments) && m.segments.some((s) => s.t !== 'text') && (
                          <details className="pd-reason"><summary>reasoning</summary>
                            <div className="pd-reason-body">
                              {m.segments.filter((s) => s.t !== 'text').map((s, j) => (s.t === 'tool'
                                ? <div key={j} className="pd-tool">{s.text}</div> : <span key={j}>{s.text}</span>))}
                            </div>
                          </details>
                        )}
                        <Md>{m.body}</Md>
                      </div>
                    </div>
                  );
                }
                if (m.role === 'manual') {
                  const cands = Array.isArray(m.candidates) ? m.candidates : [];
                  return (
                    <div key={m.id} className="pd-turn pd-turn--right pd-turn--human">
                      <div className="pd-turn-head">
                        <span className="pd-rolechip pd-rolechip--static">{m.author || 'editor'} · manual card</span>
                        {m.created_at && <span className="pd-meta">{fmtTime(m.created_at)}</span>}
                      </div>
                      <div className="pd-turn-body">
                        <div className="pd-cards">
                          {/* No gate / redo — a hand-written card never goes through the gate. */}
                          {cands.map((c, ci) => (
                            <CandidateCard key={`${c.num}-${ci}`} cand={c} chosen={isChosen(c)}
                              branch={branchOfCard(c)}
                              busy={busy || anyRunning} onChoose={(c) => decide({ cardId: c.id || undefined, num: c.num, notes: notesDraft.trim() || undefined })} />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                if (m.role === 'brief') {
                  return (
                    <React.Fragment key={m.id}>
                      {renderPastRuns()}
                      <details className="pd-fold pd-tbrief">
                        <summary>Brief — the writer's opening message · {fmtTime(m.created_at)}</summary>
                        <div className="pd-pre pd-tbrief-body">{m.body}</div>
                      </details>
                    </React.Fragment>
                  );
                }
                return renderWritingTurn(m);
              })}

              {/* With no live dialogue the brief bubble (and its fold) is absent —
                  parked past runs still need a way back in. */}
              {!writeLoop && renderPastRuns()}
              {/* Card picked but the Author hasn't written yet — an empty Author
                  bubble whose button kicks off the write. Once the loop runs, the
                  real Author/Critic/Fact-Checker bubbles take over. */}
              {canStartWrite && !writeLoop && !anyRunning && (
                <div className="pd-turn pd-turn--left pd-turn--writer">
                  <div className="pd-turn-head">
                    <span className="pd-rolechip pd-rolechip--static">Author</span>
                    <span className="pd-spacer" />
                    <button disabled={busy} onClick={startWriteStep} className="pd-btn pd-btn--sm"
                      title="One Author draft only — the loop pauses before the review, you decide what runs next">
                      Draft only
                    </button>
                    <button disabled={busy} onClick={startWrite} className="pd-btn pd-btn--primary pd-btn--sm"
                      title="Full loop: draft → review → rewrite until approved (or the round cap), then formalize">
                      Write the text
                    </button>
                  </div>
                  <div className="pd-turn-body pd-muted">
                    {!choice
                      ? 'No card picked — this point goes with no riddle by default: the Author writes a pure story, then Critic and Fact-Checker review it. Pick a card above to write around a riddle instead.'
                      : choice.mode === 'no_task'
                      ? 'This point has no riddle — the Author writes a pure story, then Critic and Fact-Checker review it.'
                      : 'The Author drafts the story from the picked card — then Critic and Fact-Checker review it.'}
                  </div>
                </div>
              )}

              {/* No Decision fork yet → the task section IS the feed bottom. */}
              {!choice && renderLive('task')}
              {renderLive('write')}
              {notice && (
                <div className="pd-tip">
                  <span>{notice}</span>
                  <button type="button" className="pd-linkish" onClick={() => setNotice(null)}>dismiss</button>
                </div>
              )}

              {showBranchTip && (
                <div className="pd-tip">
                  <span>{tipIsStory
                    ? 'Loop paused on a story — what runs next?'
                    : 'Loop paused on a critique — what runs next?'}</span>
                  {tipIsStory ? (
                    <button type="button" className="pd-tip-continue" disabled={busy} onClick={reviewOnce}
                      title="One review pass: Critic + Fact-checker judge the tip story, then the loop pauses again (an approving review finishes the point as usual)">
                      Review once
                    </button>
                  ) : (
                    <>
                      <button type="button" className="pd-tip-continue" disabled={busy} onClick={authorOnce}
                        title="One Author rewrite from the pending critique, then the loop pauses before the next review">
                        Author once
                      </button>
                      <button type="button" className="pd-tip-continue" disabled={busy} onClick={() => setManualTurn({ mode: 'append', body: '' })}
                        title="Write the Author's next version yourself — it lands as a manual turn, ready for review">
                        Write it myself…
                      </button>
                    </>
                  )}
                  <button type="button" className="pd-tip-continue" disabled={busy} onClick={continueBranch}
                    title="Continue the loop to the end: review → rewrite until approved (or the round cap), then formalize into quest fields">
                    Continue to the end
                  </button>
                </div>
              )}

              {(taskLoop && taskLoop.status === 'error' && Array.isArray(taskLoop.errors) && taskLoop.errors.length > 0) && (
                <ErrorList errors={taskLoop.errors} />
              )}
              {(writeLoop && writeLoop.status === 'error' && Array.isArray(writeLoop.errors) && writeLoop.errors.length > 0) && (
                <ErrorList errors={writeLoop.errors} />
              )}
              {((taskLoop && taskLoop.last_raw) || (writeLoop && writeLoop.last_raw)) && (
                <details className="pd-fold">
                  <summary className="pd-danger">Raw output of the failed turn</summary>
                  <div className="pd-pre pd-mono" style={{ maxHeight: 200, overflow: 'auto' }}>{(writeLoop && writeLoop.last_raw) || (taskLoop && taskLoop.last_raw)}</div>
                </details>
              )}
            </div>

            {/* The bottom bar is the ONE control shared by every stage: a note
                field + Send + Stop. All other buttons live on their own message
                bubble in the feed above. Send's meaning follows the stage and is
                disabled where no note applies. */}
            <div className="pd-actions">
              {(() => {
                const phase = writeLoop ? 'writer'
                  : (choice ? (choice.mode === 'task' ? 'brief' : 'none') : 'prebrief');
                const usesComment = phase === 'writer';
                const placeholder = phase === 'writer' ? FIELD_PLACEHOLDER.writer
                  : phase === 'none' ? 'This point has no riddle — nothing to note here.'
                  : phase === 'prebrief' ? "Note for the Author's brief — pick one of your cards above and it's folded in…"
                  : FIELD_PLACEHOLDER.brief;
                const sendLabel = phase === 'writer' ? FIELD_BTN.writer : 'Save note';
                const onSend = phase === 'writer'
                  ? sendWriterNote
                  : (phase === 'brief' ? () => decide({ num: choice.num, notes: notesDraft.trim() || undefined }) : undefined);
                const sendDisabled = busy || anyRunning || phase === 'none' || phase === 'prebrief'
                  || (phase === 'writer' && !comment.trim());
                return (
                  <div className="pd-composer">
                    <textarea className="pd-textarea"
                      disabled={phase === 'none'}
                      value={usesComment ? comment : notesDraft}
                      onChange={(e) => (usesComment ? setComment : setNotesDraft)(e.target.value)}
                      placeholder={placeholder} />
                    {usesComment && (
                      <label className="pd-steponce" title="Pause after the Author's rewrite — no review runs until you ask">
                        <input type="checkbox" checked={noteStep} onChange={(e) => setNoteStep(e.target.checked)} />
                        <span>1 turn</span>
                      </label>
                    )}
                    <button className="pd-btn pd-btn--gen" disabled={sendDisabled} onClick={onSend}>{sendLabel}</button>
                    <button className="pd-btn pd-btn--ghost pd-stop" disabled={!anyRunning} onClick={stop}>
                      {Icon.stop()}Stop
                    </button>
                  </div>
                );
              })()}
            </div>

            {writerCount > 1 && (
              <div className="pd-diffrow">
                <span className="pd-muted">Diff author turns vs previous:</span>
                <select value={diffMode} onChange={(e) => setDiffMode(e.target.value)} className="pd-select-sm">
                  <option value="off">off</option>
                  <option value="chars">characters</option>
                  <option value="words">words</option>
                  <option value="sentences">sentences</option>
                </select>
              </div>
            )}
          </>
        )}

        {promptRole && (
          <AgentPromptView role={promptRole} writeLoop={writeLoop} taskLoop={taskLoop} onClose={() => setPromptRole(null)} />
        )}
        {regen && (
          <RegenerateModal questId={questId} uuid={uuid} node={regen.node} mode={regen.mode} busy={busy}
            onClose={() => setRegen(null)} onRegenerate={regenerate} />
        )}
        {manualTurn && (
          <ManualTurnModal mode={manualTurn.mode} initial={manualTurn.body} busy={busy}
            onClose={() => setManualTurn(null)} onSave={saveManualTurn} />
        )}
      </div>
    </div>,
    document.body
  );
};

// Scoped, token-based stylesheet (professional neutral + one indigo accent, no
// emoji). Injected once with the modal; follows the editor's own theme toggle
// (<html data-theme>), not the OS scheme, so it matches the rest of the page.
const PD_CSS = `
.pd-overlay{position:fixed;inset:0;background:rgba(15,17,22,.5);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;
  --pd-ground:#eef0f4;--pd-surface:#fff;--pd-surface-2:#f5f6f9;--pd-inset:#eceef2;--pd-border:#e2e5ea;--pd-border-strong:#cdd2da;
  --pd-ink:#191c21;--pd-ink-2:#565d67;--pd-ink-3:#878e99;--pd-accent:#4f5bd5;--pd-accent-ink:#fff;--pd-accent-soft:rgba(79,91,213,.10);--pd-focus:rgba(79,91,213,.5);
  --pd-role-author:#4f5bd5;--pd-role-critic:#b7791f;--pd-role-fact:#2c7a7b;--pd-role-formal:#5a6473;--pd-role-muted:#8b929c;--pd-role-gen:#7b52c4;--pd-role-check:#3f9d63;
  --pd-ok:#2f855a;--pd-ok-soft:rgba(47,133,90,.11);--pd-warn:#b7791f;--pd-warn-soft:rgba(183,121,31,.12);--pd-danger:#c5221f;
  --pd-st-ok:#2b6cb0;--pd-st-ok-bg:rgba(43,108,176,.12);--pd-st-bad:#c05621;--pd-st-bad-bg:rgba(192,86,33,.12);--pd-st-unk:#5a6473;--pd-st-unk-bg:rgba(90,100,115,.12);
  --pd-radius:11px;--pd-radius-sm:7px;--pd-mono:ui-monospace,"SF Mono",Menlo,Consolas,monospace;
  --pd-sans:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-family:var(--pd-sans);}
html[data-theme="dark"] .pd-overlay{--pd-ground:#0e1013;--pd-surface:#1a1d22;--pd-surface-2:#20242b;--pd-inset:#161a1f;--pd-border:#2b2f37;--pd-border-strong:#3a404a;
  --pd-ink:#e7e9ec;--pd-ink-2:#a3aab4;--pd-ink-3:#6f7783;--pd-accent:#828df6;--pd-accent-ink:#12131a;--pd-accent-soft:rgba(130,141,246,.15);--pd-focus:rgba(130,141,246,.55);
  --pd-role-author:#828df6;--pd-role-critic:#d9a441;--pd-role-fact:#4fd1c5;--pd-role-formal:#8b95a6;--pd-role-gen:#b18ce6;--pd-role-check:#5bbf82;
  --pd-ok:#48bb78;--pd-ok-soft:rgba(72,187,120,.14);--pd-warn:#d9a441;--pd-warn-soft:rgba(217,164,65,.14);--pd-danger:#f56565;
  --pd-st-ok:#63b3ed;--pd-st-ok-bg:rgba(99,179,237,.16);--pd-st-bad:#f6ad55;--pd-st-bad-bg:rgba(246,173,85,.16);--pd-st-unk:#a0aec0;--pd-st-unk-bg:rgba(160,174,192,.16);}
.pd-overlay--top{z-index:1100;background:rgba(15,17,22,.55)}
.pd-modal{background:var(--pd-surface);color:var(--pd-ink);width:min(1000px,94vw);height:min(860px,90vh);display:flex;flex-direction:column;
  border:1px solid var(--pd-border);border-radius:var(--pd-radius);box-shadow:0 18px 50px rgba(20,22,28,.28);overflow:hidden;font-size:13.5px;line-height:1.5}
/* Safety net: every clickable button in the dialog gets a hand pointer; disabled
   ones fall back to the default arrow. The two variants that are buttons but not
   meant to act (the current-branch chip, a static role label) opt out. */
.pd-overlay button:not(:disabled):not(.pd-branchchip--on):not(.pd-rolechip--static){cursor:pointer}
.pd-overlay button:disabled{cursor:default}
.pd-head{display:flex;align-items:center;gap:12px;padding:14px 18px;border-bottom:1px solid var(--pd-border)}
.pd-title{font-size:15px;font-weight:620;letter-spacing:-.01em}
.pd-loc{color:var(--pd-ink-3);font-weight:500}
.pd-spacer{flex:1}
.pd-iconbtn{width:30px;height:30px;display:inline-grid;place-items:center;border-radius:var(--pd-radius-sm);border:1px solid transparent;background:transparent;color:var(--pd-ink-3);cursor:pointer}
.pd-iconbtn:hover{background:var(--pd-inset);color:var(--pd-ink);border-color:var(--pd-border)}
.pd-status{display:flex;align-items:center;gap:10px 12px;flex-wrap:wrap;padding:11px 18px;border-bottom:1px solid var(--pd-border);background:var(--pd-surface-2)}
.pd-chip{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:560;letter-spacing:.02em;padding:3px 9px;border-radius:99px;border:1px solid var(--pd-border-strong);color:var(--pd-ink-2);background:var(--pd-surface)}
.pd-dot{width:6px;height:6px;border-radius:99px;background:var(--pd-ink-3)}
.pd-chip--accent{color:var(--pd-accent);border-color:color-mix(in srgb,var(--pd-accent) 40%,var(--pd-border));background:var(--pd-accent-soft)} .pd-chip--accent .pd-dot{background:var(--pd-accent)}
.pd-chip--ok{color:var(--pd-ok);border-color:color-mix(in srgb,var(--pd-ok) 40%,var(--pd-border));background:var(--pd-ok-soft)} .pd-chip--ok .pd-dot{background:var(--pd-ok)}
.pd-chip--warn{color:var(--pd-warn);border-color:color-mix(in srgb,var(--pd-warn) 40%,var(--pd-border));background:var(--pd-warn-soft)} .pd-chip--warn .pd-dot{background:var(--pd-warn)}
.pd-chip--danger{color:var(--pd-danger);border-color:color-mix(in srgb,var(--pd-danger) 40%,var(--pd-border))} .pd-chip--danger .pd-dot{background:var(--pd-danger)}
.pd-meta{font-size:11px;color:var(--pd-ink-3);font-variant-numeric:tabular-nums}
.pd-meta--model{font-family:var(--pd-mono);font-size:10px;border:1px solid var(--pd-border);border-radius:999px;padding:0 6px;opacity:.8}
.pd-meta--live{color:var(--pd-accent)}
.pd-agents{display:flex;align-items:center;gap:3px;margin-left:auto;flex-wrap:wrap}
.pd-agenttab{font-size:11px;color:var(--pd-ink-2);background:transparent;border:none;font-family:var(--pd-mono);cursor:pointer;padding:3px 6px;border-radius:5px;display:inline-flex;gap:6px;align-items:baseline}
.pd-agenttab:hover{color:var(--pd-accent);background:var(--pd-accent-soft)}
.pd-ver{font-family:var(--pd-mono);font-size:9.5px;color:var(--pd-ink-3);opacity:.85}
.pd-ver--model{border:1px solid var(--pd-border);border-radius:999px;padding:0 5px;opacity:.75}
.pd-sep{color:var(--pd-ink-3);opacity:.5;font-size:11px}
.pd-stopbtn{display:inline-flex;align-items:center;gap:6px;font-size:11px;color:var(--pd-danger);background:transparent;border:1px solid color-mix(in srgb,var(--pd-danger) 35%,var(--pd-border));border-radius:var(--pd-radius-sm);padding:4px 10px;cursor:pointer}
.pd-feed{padding:14px 18px;display:flex;flex-direction:column;gap:12px;overflow-y:auto;flex:1;background:var(--pd-surface)}
.pd-feed>*{flex-shrink:0}
.pd-muted{color:var(--pd-ink-3)}
.pd-microlabel{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--pd-ink-3)}
.pd-pre{white-space:pre-wrap}
.pd-mono{font-family:var(--pd-mono);font-size:12px}
.pd-code-inline{background:var(--pd-inset);padding:0 4px;border-radius:4px;font-family:var(--pd-mono);font-size:.92em}
.pd-quote{margin:4px 0;padding-left:8px;border-left:3px solid var(--pd-border-strong);color:var(--pd-ink-2)}
.pd-turn{border:1px solid var(--pd-border);border-radius:var(--pd-radius);background:color-mix(in srgb,var(--pd-rail,var(--pd-ink-3)) 14%,var(--pd-surface));position:relative;overflow:hidden;max-width:86%;align-self:flex-start}
.pd-turn::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--pd-rail,var(--pd-ink-3))}
.pd-turn--left{border-bottom-left-radius:4px}
.pd-turn--right{align-self:flex-end;border-bottom-right-radius:4px}
.pd-turn--right::before{left:auto;right:0}
.pd-turn--full{max-width:100%;align-self:stretch}
.pd-turn--writer{--pd-rail:var(--pd-role-author)} .pd-turn--critic{--pd-rail:var(--pd-role-critic)} .pd-turn--factcheck{--pd-rail:var(--pd-role-fact)}
.pd-turn--formal{--pd-rail:var(--pd-role-formal)} .pd-turn--human{--pd-rail:var(--pd-role-muted)} .pd-turn--tres{--pd-rail:var(--pd-role-gen)} .pd-turn--tgen{--pd-rail:var(--pd-role-gen)} .pd-turn--tfact{--pd-rail:var(--pd-role-fact)} .pd-turn--tcheck{--pd-rail:var(--pd-role-check)}
.pd-turn--accepted{border-color:color-mix(in srgb,var(--pd-ok) 45%,var(--pd-border));box-shadow:0 0 0 1px color-mix(in srgb,var(--pd-ok) 22%,transparent)}
.pd-turn--accepted::before{background:var(--pd-ok)}
.pd-turn--live{border-style:dashed;opacity:.95}
.pd-turn-head{display:flex;align-items:center;gap:9px;padding:9px 13px 8px 15px;flex-wrap:wrap}
.pd-turn--right .pd-turn-head{flex-direction:row-reverse;padding-left:13px;padding-right:15px}
.pd-rolechip{font-family:var(--pd-mono);font-size:10.5px;font-weight:600;letter-spacing:.05em;color:var(--pd-rail,var(--pd-ink-2));padding:2px 7px;border-radius:5px;background:color-mix(in srgb,var(--pd-rail,var(--pd-ink-3)) 12%,transparent);cursor:pointer;border:none}
.pd-rolechip:hover{background:color-mix(in srgb,var(--pd-rail,var(--pd-ink-3)) 22%,transparent)}
.pd-rolechip--static{cursor:default}
.pd-turn-body{padding:0 15px 12px;line-height:1.6}
.pd-turn--right .pd-turn-body{padding-left:13px;padding-right:15px}
.pd-turn-body>*:first-child{margin-top:0}
.pd-branch{display:inline-flex;align-items:center;gap:2px;border:1px solid var(--pd-border-strong);border-radius:99px;background:var(--pd-surface);padding:1px}
.pd-branch button{width:20px;height:20px;display:grid;place-items:center;border:none;background:transparent;color:var(--pd-ink-2);cursor:pointer;border-radius:99px}
.pd-branch button:hover{background:var(--pd-inset);color:var(--pd-ink)} .pd-branch button:disabled{opacity:.35;cursor:default}
.pd-branch-count{font-size:11px;color:var(--pd-ink-2);font-variant-numeric:tabular-nums;padding:0 4px;min-width:28px;text-align:center}
.pd-pbadge{display:inline-flex;align-items:center;gap:5px;font-family:var(--pd-mono);font-size:10px;padding:2px 7px;border-radius:99px;color:var(--pd-accent);border:1px solid color-mix(in srgb,var(--pd-accent) 40%,var(--pd-border));background:var(--pd-accent-soft)}
.pd-accepted{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:600;text-transform:uppercase;letter-spacing:.03em;color:#fff;background:var(--pd-ok);border-radius:99px;padding:2px 8px}
.pd-verdict{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:560;padding:2px 9px;border-radius:99px}
.pd-verdict--ok{color:var(--pd-ok);background:var(--pd-ok-soft)} .pd-verdict--warn{color:var(--pd-warn);background:var(--pd-warn-soft)}
.pd-regenbtn{display:inline-flex;align-items:center;gap:6px;font:inherit;font-size:11px;color:var(--pd-ink-2);background:var(--pd-surface);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:4px 9px;cursor:pointer}
.pd-regenbtn:hover{border-color:var(--pd-accent);color:var(--pd-accent);background:var(--pd-accent-soft)}
/* Icon-only copy: this round's review as the Author receives it. */
.pd-fbcopy{display:inline-grid;place-items:center;width:24px;height:24px;color:var(--pd-ink-3);background:transparent;border:1px solid transparent;border-radius:var(--pd-radius-sm);cursor:pointer}
.pd-fbcopy:hover{color:var(--pd-accent);background:var(--pd-accent-soft);border-color:var(--pd-border)}
.pd-fbcopy--done{color:var(--pd-ok)}
.pd-fbcopy--done:hover{color:var(--pd-ok);background:var(--pd-ok-soft)}
.pd-formal-note{padding:0 15px 6px;display:flex;align-items:center;gap:6px;color:var(--pd-ok);font-weight:560;font-size:12px}
.pd-fold{font-size:12.5px}
.pd-fold>summary{cursor:pointer;color:var(--pd-ink-3);font-family:var(--pd-mono);font-size:11px;padding:0 15px 10px;list-style:none;display:flex;align-items:center;gap:6px}
.pd-fold>summary::before{content:"▸";color:var(--pd-ink-3)} .pd-fold[open]>summary::before{content:"▾"}
.pd-fold>summary::-webkit-details-marker{display:none}
.pd-ledger,.pd-draft{margin:0 15px 12px}
.pd-ledger{font-family:var(--pd-mono);font-size:11px;line-height:1.7;color:var(--pd-ink-2);background:var(--pd-inset);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:10px 12px;white-space:pre-wrap;overflow-x:auto}
.pd-ledgerops{margin:0 15px 10px;display:flex;flex-direction:column;gap:3px}
.pd-ledgerops--none{opacity:.75}
.pd-ledgerop{font-family:var(--pd-mono);font-size:11px;line-height:1.5;padding:3px 9px;border-radius:var(--pd-radius-sm);border:1px solid var(--pd-border);background:var(--pd-inset);white-space:pre-wrap;word-break:break-word}
.pd-ledgerop--add{color:var(--pd-ok);border-color:color-mix(in srgb,var(--pd-ok) 35%,var(--pd-border))}
.pd-ledgerop--update{color:var(--pd-warn);border-color:color-mix(in srgb,var(--pd-warn) 35%,var(--pd-border))}
.pd-ledgerop--remove{color:var(--pd-danger);border-color:color-mix(in srgb,var(--pd-danger) 35%,var(--pd-border));text-decoration:line-through}
.pd-facts{margin:0 15px 12px;display:flex;flex-direction:column;gap:4px}
/* Fixed status column so statements always start at the same x regardless of
   the status label's length (— "НЕ ПОДТВЕРЖДЁН" is the widest). */
.pd-fact{display:grid;grid-template-columns:46px 150px 1fr;gap:10px;align-items:start;font-size:12px;line-height:1.5;padding:6px 10px;border:1px solid var(--pd-border);border-left:3px solid var(--pd-border);border-radius:var(--pd-radius-sm);background:var(--pd-inset)}
.pd-fact-id{display:flex;flex-direction:column;align-items:flex-start;gap:3px;font-family:var(--pd-mono);font-size:10.5px;color:var(--pd-ink-3)}
.pd-fact-new{font-family:var(--pd-mono);font-size:8px;font-weight:700;letter-spacing:.05em;color:var(--pd-accent);background:var(--pd-accent-soft);padding:0 4px;border-radius:3px}
.pd-fact-st{display:inline-flex;align-items:center;gap:5px;justify-self:start;font-family:var(--pd-mono);font-size:9.5px;letter-spacing:.03em;text-transform:uppercase;padding:2px 8px;border-radius:999px;white-space:nowrap}
.pd-fact-ico{font-size:11px;line-height:1;font-weight:700}
.pd-fact-st--ok{color:var(--pd-st-ok);background:var(--pd-st-ok-bg)}
.pd-fact-st--bad{color:var(--pd-st-bad);background:var(--pd-st-bad-bg)}
.pd-fact-st--unknown{color:var(--pd-st-unk);background:var(--pd-st-unk-bg)}
.pd-fact-main{min-width:0}
.pd-fact-txt{color:var(--pd-ink);word-break:break-word}
.pd-fact-note{margin-top:2px;font-size:11px;color:var(--pd-ink-3);font-style:italic;word-break:break-word}
.pd-fact-src{margin-top:2px;font-size:11px}
.pd-fact-src a{color:var(--pd-accent);text-decoration:underline}
.pd-fact-src--plain{color:var(--pd-ink-3);word-break:break-all}
/* Diff state is carried by the NEW badge (added) and strike-through (removed),
   not colour alone — the tint/border is only a secondary cue. */
.pd-fact--add{border-left-color:var(--pd-accent);background:var(--pd-accent-soft)}
.pd-fact--del{opacity:.6;border-left-color:var(--pd-border-strong)}
.pd-fact--del .pd-fact-txt,.pd-fact--del .pd-fact-note{text-decoration:line-through}
.pd-fact--del .pd-fact-src{opacity:.7}
/* Critic findings — reuse the fact card look with a 2-column grid (kind badge
   + finding) since there is no id column. */
.pd-crit-sum{font-size:12.5px;line-height:1.55;color:var(--pd-ink);word-break:break-word;margin-bottom:2px}
.pd-critrow{grid-template-columns:96px 1fr}
.pd-critrow--blocker{border-left-color:color-mix(in srgb,var(--pd-st-bad) 55%,var(--pd-border))}
.pd-crit-type{font-family:var(--pd-mono);font-size:9.5px;color:var(--pd-ink-3);background:var(--pd-inset);border:1px solid var(--pd-border);border-radius:3px;padding:0 4px;margin-right:6px;font-style:normal}
.pd-crit-prose{color:var(--pd-ink-2)}
.pd-draft-field{margin-bottom:8px}
.pd-reason{margin:6px 0}
.pd-reason>summary{cursor:pointer;font-size:11px;color:var(--pd-ink-3);font-family:var(--pd-mono)}
.pd-reason-body{font-style:italic;color:var(--pd-ink-2);white-space:pre-wrap;font-size:12px;max-height:180px;overflow:auto;margin-top:4px}
.pd-tool{color:var(--pd-accent);font-style:normal}
.pd-tool::before{content:"web · ";opacity:.7}
.pd-tbrief{background:var(--pd-inset);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);align-self:stretch;max-width:100%}
.pd-pastruns{background:var(--pd-inset);border:1px dashed var(--pd-border);border-radius:var(--pd-radius-sm);align-self:stretch;max-width:100%;padding-top:8px}
.pd-pastrun-row{display:flex;align-items:center;gap:8px;padding:3px 15px 8px;font-size:12px}
.pd-tbrief>summary{padding:8px 12px}
.pd-tbrief-body{padding:0 12px 10px;color:var(--pd-ink-2)}
.pd-cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
@media (max-width:620px){.pd-cards{grid-template-columns:repeat(2,minmax(0,1fr))}}
.pd-cardedit{display:flex;flex-direction:column;gap:7px;margin-bottom:4px}
.pd-cardedit .pd-textarea{width:100%;min-width:0;flex:none}
.pd-btn--danger{color:var(--pd-danger);border-color:color-mix(in srgb,var(--pd-danger) 35%,var(--pd-border))}
.pd-btn--danger:hover:not(:disabled){background:color-mix(in srgb,var(--pd-danger) 12%,transparent);border-color:var(--pd-danger)}
.pd-card{border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:8px 10px;font-size:13px;background:var(--pd-surface)}
.pd-card--chosen{border-color:color-mix(in srgb,var(--pd-ok) 50%,var(--pd-border));background:var(--pd-ok-soft)}
.pd-card--killed{border-color:color-mix(in srgb,var(--pd-danger) 40%,var(--pd-border));opacity:.8}
.pd-card--live{border-style:dashed}
.pd-card-head{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;margin-bottom:4px}
.pd-card-num{font-weight:700}
.pd-cardid{font-family:ui-monospace,Menlo,monospace;font-size:10px;color:var(--pd-ink-3);letter-spacing:.02em;cursor:default}
.pd-card-chosen{color:var(--pd-ok);font-weight:700;font-size:11px}
.pd-card-flags{color:var(--pd-danger);font-size:11px;margin-bottom:4px}
.pd-card-q{margin-bottom:4px;white-space:pre-wrap}
.pd-card-answer{font-family:var(--pd-mono);color:var(--pd-ok);margin-bottom:4px}
.pd-card-payoff{font-size:12px;white-space:pre-wrap}
.pd-fold-sm{margin-bottom:4px} .pd-fold-sm>summary{cursor:pointer;font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:var(--pd-ink-3)}
.pd-tag{font-size:9.5px;text-transform:uppercase;letter-spacing:.04em;padding:1px 7px;border-radius:99px;font-weight:600}
.pd-tag--ok{background:var(--pd-ok);color:#fff} .pd-tag--accent{background:var(--pd-accent);color:var(--pd-accent-ink)} .pd-tag--warn{background:var(--pd-warn);color:#fff}
.pd-tag--danger{background:var(--pd-danger);color:#fff} .pd-tag--muted{background:var(--pd-role-muted);color:#fff}
.pd-tag--ghost{background:transparent;border:1px solid var(--pd-border-strong);color:var(--pd-ink-3)}
.pd-tag--ghost-ok{background:transparent;border:1px solid color-mix(in srgb,var(--pd-ok) 45%,var(--pd-border));color:var(--pd-ok)}
.pd-tag--role{background:color-mix(in srgb,var(--pd-role-author) 12%,transparent);color:var(--pd-role-author);font-family:var(--pd-mono);text-transform:none;letter-spacing:0}
.pd-decision{border:1px solid color-mix(in srgb,var(--pd-ok) 45%,var(--pd-border));border-radius:var(--pd-radius);padding:9px 13px;background:var(--pd-ok-soft);align-self:stretch}
.pd-decision--none{border-color:var(--pd-border-strong);background:var(--pd-inset)}
.pd-decision-head{font-weight:600;color:var(--pd-ok)} .pd-decision--none .pd-decision-head{color:var(--pd-ink-2)}
.pd-decision-note{font-size:12px;color:var(--pd-ink-2);margin:4px 0 6px;white-space:pre-wrap}
.pd-decision-branches{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:6px 0}
.pd-branchchip{font:inherit;font-size:11.5px;font-weight:600;padding:2px 10px;border-radius:99px;cursor:pointer;border:1px solid var(--pd-border-strong);background:var(--pd-surface);color:var(--pd-ink-2)}
.pd-branchchip:hover:not(:disabled){border-color:var(--pd-ink-3);color:var(--pd-ink)}
.pd-branchchip--on{border-color:var(--pd-ok);color:var(--pd-ok);background:var(--pd-ok-soft);cursor:default}
.pd-branchchip:disabled:not(.pd-branchchip--on){opacity:.5;cursor:default}
.pd-gate{border:1px solid var(--pd-border);border-radius:var(--pd-radius);padding:7px 13px;background:var(--pd-surface-2);font-size:12px;align-self:stretch}
.pd-gate--dead{border-color:color-mix(in srgb,var(--pd-danger) 40%,var(--pd-border))}
.pd-gate-title{font-weight:600}
.pd-linkish{background:none;border:none;color:var(--pd-accent);cursor:pointer;font:inherit;padding:0;text-decoration:underline}
.pd-tip{align-self:flex-start;max-width:86%;display:flex;align-items:center;gap:12px;flex-wrap:wrap;border:1px dashed var(--pd-border-strong);border-radius:var(--pd-radius);padding:10px 14px;color:var(--pd-ink-3);font-size:12.5px;background:color-mix(in srgb,var(--pd-ink-3) 4%,transparent)}
.pd-tip-continue{display:inline-flex;align-items:center;gap:8px;font:inherit;font-size:11px;color:var(--pd-ink-3);background:var(--pd-surface);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:5px 10px;cursor:pointer;opacity:.85}
.pd-tip-continue:hover:not(:disabled){border-color:var(--pd-ink-3);color:var(--pd-ink);opacity:1}
.pd-tip-continue:disabled{opacity:.5;cursor:default}
.pd-soon{font-family:var(--pd-mono);font-size:9px;text-transform:uppercase;letter-spacing:.05em;border:1px solid var(--pd-border-strong);border-radius:4px;padding:1px 4px}
.pd-diff-add{background:var(--pd-ok-soft)} .pd-diff-del{background:var(--pd-warn-soft);text-decoration:line-through;opacity:.65}
.pd-errlist{color:var(--pd-danger);font-size:12px;margin:4px 0;padding-left:18px}
.pd-danger{color:var(--pd-danger)}
.pd-actions{border-top:1px solid var(--pd-border);padding:13px 18px;display:flex;gap:10px;align-items:flex-start;background:var(--pd-surface-2);flex-wrap:wrap}
.pd-composer{flex:1;min-width:240px;display:flex;gap:8px;align-items:flex-start;flex-wrap:wrap}
.pd-steponce{display:inline-flex;align-items:center;gap:5px;align-self:center;font-size:11px;color:var(--pd-ink-3);white-space:nowrap;user-select:none;cursor:pointer}
.pd-steponce input{accent-color:var(--pd-accent);margin:0;cursor:pointer}
.pd-steponce:hover{color:var(--pd-ink)}
.pd-stop{display:inline-flex;align-items:center;gap:6px;color:var(--pd-danger);border-color:color-mix(in srgb,var(--pd-danger) 35%,var(--pd-border))}
.pd-stop:disabled{opacity:.4;color:var(--pd-ink-3);border-color:var(--pd-border)}
.pd-card--notask{border-style:dashed;background:var(--pd-inset)}
/* --- Researcher bubble: focus field + variant tag --- */
.pd-focusrow{padding:6px 14px 0}
.pd-focus{width:100%;box-sizing:border-box;font:inherit;font-size:12.5px;padding:5px 8px;border:1px solid var(--pd-border-strong);border-radius:6px;background:var(--pd-surface);color:var(--pd-ink)}
.pd-focus:disabled{opacity:.6}
.pd-focustag{font-size:11px;font-weight:600;color:var(--pd-ok);background:var(--pd-ok-soft);border-radius:99px;padding:1px 8px;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pd-focustag--none{color:var(--pd-ink-2);background:var(--pd-inset);font-weight:500}
.pd-manual{flex-basis:100%;display:flex;flex-direction:column;gap:6px;padding:10px;border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);background:var(--pd-surface)}
.pd-manual-t{font-size:11.5px;color:var(--pd-ink-3)}
.pd-manual .pd-textarea{min-width:0;width:100%;flex:none}
.pd-manual-row{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.pd-manual-actions{display:flex;gap:8px}
.pd-textarea{flex:1;min-width:220px;min-height:46px;box-sizing:border-box;resize:vertical;font:inherit;font-size:12.5px;color:var(--pd-ink);background:var(--pd-surface);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:8px 10px}
.pd-textarea::placeholder{color:var(--pd-ink-3)} .pd-textarea:focus-visible{outline:2px solid var(--pd-focus);border-color:var(--pd-accent)}
.pd-btn{font:inherit;font-size:12.5px;font-weight:540;border-radius:var(--pd-radius-sm);padding:8px 15px;cursor:pointer;border:1px solid var(--pd-border-strong);background:var(--pd-surface);color:var(--pd-ink)}
.pd-btn:hover{border-color:var(--pd-ink-3)} .pd-btn:disabled{opacity:.5;cursor:default}
.pd-btn--sm{padding:4px 12px;font-size:12px}
.pd-btn--primary{background:var(--pd-accent);border-color:var(--pd-accent);color:var(--pd-accent-ink)} .pd-btn--primary:hover{filter:brightness(1.06);border-color:var(--pd-accent)}
.pd-btn--ok{background:var(--pd-ok);border-color:var(--pd-ok);color:#fff} .pd-btn--ok:hover{filter:brightness(1.06);border-color:var(--pd-ok)}
.pd-btn--gen{background:var(--pd-role-gen);border-color:var(--pd-role-gen);color:#fff} .pd-btn--gen:hover{filter:brightness(1.06);border-color:var(--pd-role-gen)}
.pd-btn--ghost{background:transparent}
.pd-diffrow{display:flex;align-items:center;gap:8px;font-size:12px;padding:8px 18px;border-top:1px solid var(--pd-border);background:var(--pd-surface-2)}
.pd-select-sm,select.pd-select-sm{font:inherit;font-size:12px;padding:3px 6px;border-radius:6px;border:1px solid var(--pd-border-strong);background:var(--pd-surface);color:var(--pd-ink)}
.pd-error{color:var(--pd-danger);font-size:12.5px}
.pd-error--bar{padding:10px 18px;border-bottom:1px solid var(--pd-border)}
.pd-panel{width:min(720px,94vw);max-height:90vh;overflow:auto;background:var(--pd-surface);color:var(--pd-ink);border:1px solid var(--pd-border);border-radius:var(--pd-radius);box-shadow:0 18px 50px rgba(20,22,28,.3)}
.pd-panel-head{display:flex;align-items:center;gap:12px;padding:16px 18px 4px}
.pd-panel-head h3{margin:0;font-size:15px;font-weight:620;display:flex;align-items:center;gap:8px;flex:1}
.pd-panel-sub{padding:2px 18px 0;color:var(--pd-ink-2);font-size:12.5px}
.pd-panel-body{padding:12px 18px 4px;display:flex;flex-direction:column;gap:14px}
.pd-freshtoggle{display:flex;align-items:center;gap:8px;margin:-2px 0 -4px;font-size:12px;color:var(--pd-ink-2);cursor:pointer}
.pd-freshtoggle input{cursor:pointer;margin:0}
.pd-field label{display:block;font-family:var(--pd-mono);font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;color:var(--pd-ink-3);margin-bottom:6px}
.pd-field .pd-hint{text-transform:none;letter-spacing:0;opacity:.85}
.pd-codeview{font-family:var(--pd-mono);font-size:12px;line-height:1.55;white-space:pre-wrap;background:var(--pd-inset);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:11px 12px;max-height:320px;overflow:auto}
.pd-panel .pd-textarea{width:100%;background:var(--pd-inset)}
.pd-panel-foot{padding:14px 18px 18px;display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.pd-modelpick{display:flex;align-items:center;gap:8px} .pd-modelpick label{font-family:var(--pd-mono);font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;color:var(--pd-ink-3)}
.pd-modelpick select{font:inherit;font-size:12.5px;padding:7px 9px;border-radius:var(--pd-radius-sm);border:1px solid var(--pd-border-strong);background:var(--pd-surface);color:var(--pd-ink)}
/* --- agent Context diagram --- */
.pd-cx-wrap{margin-bottom:14px}
.pd-cx-title{font-family:var(--pd-mono);font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;color:var(--pd-ink-3);margin-bottom:8px}
.pd-cx{border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);background:var(--pd-inset);padding:12px}
.pd-cx--loading{color:var(--pd-ink-3);font-size:12px}
.pd-cx-session{display:flex;flex-wrap:wrap;align-items:baseline;gap:6px 10px;margin-bottom:12px;padding-bottom:10px;border-bottom:1px dashed var(--pd-border)}
.pd-cx-sbadge{font-size:11.5px;font-weight:600;padding:2px 8px;border-radius:999px;white-space:nowrap}
.pd-cx-session--resumed .pd-cx-sbadge{background:rgba(90,170,120,.18);color:#3f9d63}
.pd-cx-session--fresh .pd-cx-sbadge{background:var(--pd-surface);color:var(--pd-ink-3);border:1px solid var(--pd-border)}
.pd-cx-snote{font-size:12px;color:var(--pd-ink-2)}
.pd-cx-flow{display:flex;align-items:center;gap:12px}
.pd-cx-sources{flex:1 1 auto;display:flex;flex-direction:column;gap:6px;min-width:0}
.pd-cx-src{display:flex;align-items:flex-start;gap:8px;background:var(--pd-surface);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:6px 9px}
.pd-cx-src--extra{border-style:dashed;opacity:.9}
.pd-cx-dot{flex:none;width:9px;height:9px;border-radius:50%;margin-top:4px;background:var(--pd-ink-3)}
.pd-cx-src--self .pd-cx-dot{background:#e08a3c}
.pd-cx-src--agent .pd-cx-dot{background:#7b6ccf}
.pd-cx-src--human .pd-cx-dot{background:#9aa0a6}
.pd-cx-src--input .pd-cx-dot{background:#4f8fd0}
.pd-cx-srctext{display:flex;flex-direction:column;gap:1px;min-width:0}
.pd-cx-label{font-size:12.5px;color:var(--pd-ink)}
.pd-cx-key{font-family:var(--pd-mono);font-size:10.5px;color:var(--pd-ink-3)}
.pd-cx-key--extra{font-style:italic}
.pd-cx-empty{font-size:12px;color:var(--pd-ink-3);font-style:italic}
.pd-cx-arrow{flex:none;color:var(--pd-ink-3);font-size:18px}
.pd-cx-agent{flex:none;align-self:center;font-weight:600;font-size:13px;color:var(--pd-ink);background:var(--pd-surface);border:1.5px solid var(--pd-border-strong);border-radius:var(--pd-radius-sm);padding:10px 14px;text-align:center;max-width:160px}
.pd-cx-legend{display:flex;flex-wrap:wrap;gap:6px 14px;margin-top:12px;padding-top:10px;border-top:1px dashed var(--pd-border)}
.pd-cx-leg{display:inline-flex;align-items:center;gap:6px;font-size:11px;color:var(--pd-ink-3)}
.pd-cx-leg .pd-cx-dot{margin-top:0}
/* --- per-card gate notes + actions --- */
.pd-card-actions{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.pd-card--redone{opacity:.72}
.pd-gatenotes{margin:6px 0;border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);background:var(--pd-inset);padding:6px 9px}
.pd-gatenotes>summary{cursor:pointer;font-size:11.5px;color:var(--pd-ink-2);user-select:none}
.pd-gatenote{margin-top:7px;padding-top:7px;border-top:1px dashed var(--pd-border)}
.pd-gatenote:first-of-type{margin-top:7px}
.pd-gatenote-t{font-size:12px;font-weight:600;color:var(--pd-ink)}
.pd-gatenote-m{font-weight:700}
.pd-gatenote--ok .pd-gatenote-m{color:#3f9d63}
.pd-gatenote--bad .pd-gatenote-m{color:#d2544a}
.pd-gatenote-b{font-size:12px;color:var(--pd-ink-2);white-space:pre-wrap;margin-top:2px}
.pd-ver{display:inline-flex;align-items:center;gap:2px;margin-left:2px}
.pd-ver-btn{border:1px solid var(--pd-border);background:var(--pd-surface);color:var(--pd-ink-2);border-radius:var(--pd-radius-sm);width:20px;height:20px;line-height:1;cursor:pointer;font-size:13px;padding:0}
.pd-ver-btn:disabled{opacity:.4;cursor:default}
.pd-ver-n{font-family:var(--pd-mono);font-size:10.5px;color:var(--pd-ink-3);white-space:nowrap}
/* agent-coloured card actions: button hue matches the agent's bubble rail */
.pd-card-actions-sep{width:1px;align-self:stretch;background:var(--pd-border);margin:1px 3px}
.pd-btn--check{background:transparent;border:1px solid color-mix(in srgb,var(--pd-role-check) 55%,var(--pd-border));color:var(--pd-role-check)}
.pd-btn--check:hover:not(:disabled){background:color-mix(in srgb,var(--pd-role-check) 14%,transparent)}
.pd-btn--gen{background:transparent;border:1px solid color-mix(in srgb,var(--pd-role-gen) 55%,var(--pd-border));color:var(--pd-role-gen)}
.pd-btn--gen:hover:not(:disabled){background:color-mix(in srgb,var(--pd-role-gen) 14%,transparent)}
/* --- Researcher bubble: download link --- */
.pd-dlbtn{display:inline-flex;align-items:center;gap:5px;font:inherit;font-size:11px;color:var(--pd-role-gen);background:transparent;border:1px solid color-mix(in srgb,var(--pd-role-gen) 45%,var(--pd-border));border-radius:var(--pd-radius-sm);padding:3px 9px;cursor:pointer}
.pd-dlbtn:hover:not(:disabled){background:color-mix(in srgb,var(--pd-role-gen) 14%,transparent)}
/* Copy-guide button: label swap without a width change (both labels overlaid in
   one grid cell) + a green "done" flash for a couple seconds after copying. */
.pd-copyswap{display:inline-grid}
.pd-copyswap>span{grid-area:1/1;white-space:nowrap}
.pd-copyswap-done{visibility:hidden}
.pd-copybtn--done .pd-copyswap-idle{visibility:hidden}
.pd-copybtn--done .pd-copyswap-done{visibility:visible}
.pd-copybtn--done{color:var(--pd-ok);border-color:color-mix(in srgb,var(--pd-ok) 55%,var(--pd-border))}
.pd-copybtn--done:hover:not(:disabled){background:color-mix(in srgb,var(--pd-ok) 14%,transparent)}
/* --- live-turn activity: current-activity line (search query / spawn note) --- */
.pd-livestatus{display:flex;align-items:center;gap:7px;font-family:var(--pd-mono);font-size:11px;color:var(--pd-ink-2);background:var(--pd-inset);border:1px solid var(--pd-border);border-radius:var(--pd-radius-sm);padding:5px 9px;margin-bottom:8px;word-break:break-word}
.pd-livedot{flex:none;width:7px;height:7px;border-radius:50%;background:var(--pd-accent)}
/* --- task stage (facts + your cards), shown before a card is picked --- */
.pd-taskstage{flex-basis:100%;display:flex;flex-direction:column;gap:12px}
.pd-stagebar{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.pd-stagehint{font-size:11.5px;color:var(--pd-ink-3);flex:1;min-width:180px;line-height:1.45}
.pd-cardstage{border:1px solid var(--pd-border);border-radius:var(--pd-radius);background:var(--pd-surface);padding:10px 12px}
.pd-cardstage-head{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.pd-cardstage-title{font-size:12.5px;font-weight:620;letter-spacing:-.01em}
.pd-cardstage-count{font-size:10.5px;font-weight:600;color:var(--pd-ok);background:var(--pd-ok-soft);border-radius:99px;padding:2px 8px}
.pd-cardstage-hint{font-size:11.5px;color:var(--pd-ink-3)}
.pd-addcard{margin-left:auto;display:inline-flex;align-items:center;gap:6px;font:inherit;font-size:12px;font-weight:540;color:var(--pd-accent);background:var(--pd-accent-soft);border:1px dashed color-mix(in srgb,var(--pd-accent) 45%,var(--pd-border));border-radius:var(--pd-radius-sm);padding:5px 12px;cursor:pointer}
.pd-addcard:hover:not(:disabled){background:color-mix(in srgb,var(--pd-accent) 16%,transparent);border-style:solid}
.pd-addcard:disabled{opacity:.5;cursor:default}
.pd-typepills{display:flex;gap:6px;flex-wrap:wrap}
.pd-pill{font:inherit;font-size:11px;font-weight:560;color:var(--pd-ink-2);background:var(--pd-surface);border:1px solid var(--pd-border-strong);border-radius:99px;padding:4px 12px;cursor:pointer}
.pd-pill:hover{border-color:var(--pd-ink-3);color:var(--pd-ink)}
.pd-pill--on{color:var(--pd-accent-ink);background:var(--pd-accent);border-color:var(--pd-accent)}
`;

export default PointDialogModal;
